import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { deduplicateArticles, resolveGroups } from './data/dedup';
import { CATEGORIES } from './data/labels';
import {
  supabase, fetchAdminRole,
  fetchHiddenIds, hideArticle, unhideArticle,
  fetchEditedArticles, saveEditedArticle, deleteEditedArticle,
  fetchCustomArticles, saveCustomArticle, deleteCustomArticle,
  logAction, fetchAuditLog, clearAuditLog,
  fetchSharedArticle,
  fetchAdsEnabledConfig, saveAdsEnabledConfig,
} from './supabase';
import { refreshAdsEnabled } from './ads';
import './App.css';

const API_BASE = Capacitor.isNativePlatform() ? 'https://www.blitz-news.ro' : '';
// Always the real public domain — used for links we share with other people,
// which must work outside the app/dev server regardless of where this code
// is currently running.
const SITE_URL = 'https://www.blitz-news.ro';

async function getShareUrl(article) {
  const res = await fetch(`${API_BASE}/api/share`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id: article.id, source: article.source, link: article.link }),
  });
  const data = await res.json();
  if (!res.ok || !data.code) throw new Error(data.error || 'Could not create share link');
  return `${SITE_URL}/s/${data.code}`;
}
const SOURCES = ['digi24', 'protv', 'hotnews', 'recorder'];

// In-memory cache of fetched article content, keyed by article link.
// Lives for as long as the app/tab is open — reopening an article this
// session (prefetched or already read) skips the network call entirely.
const articleContentCache = new Map();
// Only one prefetch in flight at a time: touching a new card cancels
// whatever the previous card was still fetching.
let prefetchController = null;
let prefetchingLink = null;

function prefetchArticle(article) {
  if (!article || article.isCustom) return; // custom articles have their body inline already
  const link = article.link;
  if (!link || articleContentCache.has(link) || prefetchingLink === link) return;

  if (prefetchController) prefetchController.abort();
  const controller = new AbortController();
  prefetchController = controller;
  prefetchingLink = link;

  fetch(`${API_BASE}/api/article?url=${encodeURIComponent(link)}`, { signal: controller.signal })
    .then(r => r.json())
    .then(data => {
      if (!data.error && data.content) articleContentCache.set(link, data.content);
    })
    .catch(() => {}) // aborted or offline — the reader will just fetch normally when opened
    .finally(() => {
      if (prefetchingLink === link) prefetchingLink = null;
    });
}
const getAppName = () => localStorage.getItem('blitz_app_name') || 'Blitz';
const SOURCE_META = {
  digi24:   { label: 'Digi24' },
  protv:    { label: 'ProTV' },
  hotnews:  { label: 'HotNews' },
  recorder: { label: 'Recorder' },
};
const READ_KEY = 'blitz_read_ids';
const FEED_KEY = 'blitz_feed';
const EXPIRY_MS = 12 * 60 * 60 * 1000; // 12 hours

// ─── localStorage helpers ─────────────────────────────────────────────────────

function getReadIds() {
  try {
    const raw = localStorage.getItem(READ_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    const now = Date.now();
    const cleaned = {};
    for (const [id, ts] of Object.entries(parsed)) {
      // Only keep valid numeric timestamps within expiry
      if (typeof ts === 'number' && ts > 0 && now - ts < EXPIRY_MS) {
        cleaned[id] = ts;
      }
    }
    return cleaned;
  } catch { return {}; }
}

function markAsRead(id) {
  try {
    const current = getReadIds();
    current[id] = Date.now();
    localStorage.setItem(READ_KEY, JSON.stringify(current));
  } catch {}
}

function loadFeed() {
  // Returns { entries: [...], seenIds: {id: fetchedAt} } or null
  try {
    const raw = localStorage.getItem(FEED_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // If stored feed is older than 24h, discard it
    if (Date.now() - parsed.savedAt > EXPIRY_MS) {
      localStorage.removeItem(FEED_KEY);
      return null;
    }
    return parsed;
  } catch { return null; }
}

function saveFeed(entries, seenIds) {
  try {
    localStorage.setItem(FEED_KEY, JSON.stringify({
      entries,
      seenIds,
      savedAt: Date.now(),
    }));
  } catch {}
}

// ─── Article Reader ───────────────────────────────────────────────────────────

function ArticleReader({ entry, onBack, showImage, openShareId, setOpenShareId }) {
  const { article, alsoOn } = entry;
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    // Custom articles have body stored directly — no need to fetch
    if (article.isCustom && article.body) {
      setContent(article.body.replace(/\n/g, '<br/>'));
      setLoading(false);
      return;
    }

    // Already prefetched (or read before this session) — show it instantly.
    const cached = articleContentCache.get(article.link);
    if (cached) {
      setContent(cached);
      setFailed(false);
      setLoading(false);
      return;
    }

    setLoading(true);
    setFailed(false);

    fetch(`${API_BASE}/api/article?url=${encodeURIComponent(article.link)}`)
      .then(r => r.json())
      .then(data => {
        if (data.error || !data.content) {
          setFailed(true);
          setContent(null);
        } else {
          articleContentCache.set(article.link, data.content);
          setContent(data.content);
        }
      })
      .catch(() => { setFailed(true); setContent(null); })
      .finally(() => setLoading(false));
  }, [article]);

  const timeAgo = formatTimeAgo(article.pubDate);

  return (
    <div className="reader">
      <div className="back-btn-bar" style={{display:'flex',alignItems:'center',justifyContent:'space-between'}}>
        <button className="back-btn" onClick={onBack}>
          &larr; Inapoi
        </button>
        <div style={{paddingRight:'8px'}}>
          <ShareButton article={article} openShareId={openShareId} setOpenShareId={setOpenShareId} popupPosition="down" />
        </div>
      </div>

      {showImage && article.image && (
        <div className="reader-image-wrap">
          <img src={article.image} alt={article.title} className="reader-image" />
        </div>
      )}

      <div className="reader-content">
        <div className="reader-meta">
          <span className="reader-source-badge">{article.sourceName}</span>
          {article.isSponsored && (
            <span style={{fontSize:'11px',fontWeight:'700',padding:'2px 8px',borderRadius:'4px',background:'rgba(245,158,11,0.15)',color:'#f59e0b',letterSpacing:'0.3px'}}>Sponsorizat</span>
          )}
          <span className="reader-time">{timeAgo}</span>
        </div>

        <h1 className="reader-title">{article.title}</h1>

        {article.description && (
          <p className="reader-excerpt">{article.description}</p>
        )}

        {article.description && !loading && !article.isCustom && (
          <div className="reader-divider" />
        )}

        {loading ? (
          <div className="reader-loading"><div className="spinner" /></div>
        ) : failed ? (
          <div className="reader-fallback-notice">
            Continutul complet nu a putut fi incarcat.
          </div>
        ) : (
          <div className="reader-body" dangerouslySetInnerHTML={{ __html: (showImage ? content : content.replace(/<img[^>]*>/gi, '')).replace(/<a /gi, '<a target="_blank" rel="noreferrer" ') }} />
        )}

        {alsoOn.length > 0 && (
          <div className="also-on">
            <span className="also-on-label">Stirea apare si pe:</span>
            {alsoOn.map((s) => (
              <a key={s.source} href={s.link} target="_blank" rel="noreferrer" className="also-on-link">
                {s.sourceName}
              </a>
            ))}
          </div>
        )}

        <div className="reader-footer">
          <div className="reader-footer-row">
            <span className="reader-footer-label">Sursa:</span>
            <a href={article.link} target="_blank" rel="noreferrer" className="reader-footer-source">
              {article.sourceName} &rarr;
            </a>
          </div>
          {article.isCustom && (
            <>
              <div className="reader-footer-row">
                <span className="reader-footer-label">Creat la:</span>
                <span className="reader-footer-source" style={{color:'var(--text-muted)',fontWeight:'normal'}}>{new Date(article.pubDate).toLocaleString('ro-RO', {day:'2-digit',month:'2-digit',year:'numeric',hour:'2-digit',minute:'2-digit'})}</span>
              </div>
              {article.updatedAt && article.updatedAt !== article.pubDate && (
                <div className="reader-footer-row">
                  <span className="reader-footer-label">Actualizat la:</span>
                  <span className="reader-footer-source" style={{color:'var(--text-muted)',fontWeight:'normal'}}>{new Date(article.updatedAt).toLocaleString('ro-RO', {day:'2-digit',month:'2-digit',year:'numeric',hour:'2-digit',minute:'2-digit'})}</span>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Share Button ───────────────────────────────────────────────────────────────
// Used by both the feed card and the full article page, so the two never
// drift out of sync. `openShareId`/`setOpenShareId` are lifted to the top
// App component so only one share popup is ever open at once, and the
// Android back button can close it.

function ShareButton({ article, openShareId, setOpenShareId, popupPosition = 'up' }) {
  const shareOpen = openShareId === article.id;
  const [copied, setCopied] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [shareError, setShareError] = useState('');

  const toggleShare = (e) => {
    e.stopPropagation();
    setShareError('');
    setOpenShareId(shareOpen ? null : article.id);
  };

  const getUrl = async () => {
    setSharing(true);
    setShareError('');
    try {
      return await getShareUrl(article);
    } catch {
      setShareError('Nu am putut genera linkul, încearcă din nou.');
      return null;
    } finally {
      setSharing(false);
    }
  };

  const handleCopy = async (e) => {
    e.stopPropagation();
    const url = await getUrl();
    if (!url) return;
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => { setCopied(false); setOpenShareId(null); }, 1500);
    });
  };

  const handleNativeShare = async (e) => {
    e.stopPropagation();
    const url = await getUrl();
    if (!url) return;
    navigator.share({ title: article.title, url })
      .then(() => setOpenShareId(null))
      .catch(() => {}); // user cancelled the native share sheet — not an error
  };

  return (
    <div className="card-share-wrap">
      <button className="card-share-btn" onClick={toggleShare} aria-label="Distribuie">
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
          <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
        </svg>
      </button>
      {shareOpen && (
        <div className={`share-popup ${popupPosition === 'down' ? 'below' : ''}`} onClick={e => e.stopPropagation()}>
          <button className="share-option" onClick={handleCopy} disabled={sharing}>
            <span className="share-option-icon">📋</span>
            <span>{sharing ? 'Se generează...' : copied ? 'Copiat!' : 'Copiază linkul'}</span>
          </button>
          {navigator.share && (
            <button className="share-option" onClick={handleNativeShare} disabled={sharing}>
              <span className="share-option-icon">↗️</span>
              <span>{sharing ? 'Se generează...' : 'Distribuie'}</span>
            </button>
          )}
          {shareError && <span style={{fontSize:'11px',color:'#e05555',padding:'4px 12px',display:'block'}}>{shareError}</span>}
        </div>
      )}
    </div>
  );
}

// ─── Article Card ─────────────────────────────────────────────────────────────

function ArticleCard({ entry, onClick, isRead, showImage, openShareId, setOpenShareId }) {
  const { article, alsoOn } = entry;
  const timeAgo = formatTimeAgo(article.isCustom && article.updatedAt ? article.updatedAt : article.pubDate);

  const handleCardClick = () => {
    if (openShareId) { return; }
    onClick();
  };

  const handlePrefetch = () => prefetchArticle(article);

  return (
    <article
      className={`card ${isRead ? 'card-read' : ''}`}
      onClick={handleCardClick}
      onTouchStart={handlePrefetch}
      onPointerDown={handlePrefetch}
    >
      {showImage && article.image && (
        <div className="card-image-wrap">
          <img src={article.image} alt={article.title} className="card-image" loading="lazy" />
        </div>
      )}
      <div className="card-body">
        <div className="card-meta">
          <span className={`source-badge source-${article.source}`}>{article.sourceName}</span>
          {article.isSponsored && <span style={{fontSize:"10px",fontWeight:"700",padding:"2px 7px",borderRadius:"4px",background:"rgba(245,158,11,0.15)",color:"#f59e0b",letterSpacing:"0.3px"}}>Sponsorizat</span>}
          <div className="card-status">
            <span className="card-status-time">{timeAgo}</span>
            <span className={`card-status-dot ${isRead ? 'read' : 'unread'}`} title={isRead ? 'Citit' : 'Necitit'} />
          </div>
        </div>
        <h2 className="card-title">{article.title}</h2>
        {article.description && article.description.length > 50 && (
          <p className="card-desc">{article.description.slice(0, 120)}...</p>
        )}
        <div className="card-footer">
          {alsoOn.length > 0 && (
            <div className="card-also">
              <span className="card-also-dot" />
              Si pe {alsoOn.map(s => s.sourceName).join(', ')}
            </div>
          )}
          <ShareButton article={article} openShareId={openShareId} setOpenShareId={setOpenShareId} />
        </div>
      </div>
    </article>
  );
}

// ─── Skeleton ─────────────────────────────────────────────────────────────────

function FilterPanel({ activeFilters, onToggle, onClear, onClose }) {
  const all = Object.entries(CATEGORIES);

  return (
    <div className="filter-overlay" onClick={onClose}>
      <div className="filter-panel" onClick={e => e.stopPropagation()}>
        <button
          className={`filter-row ${activeFilters.length === 0 ? 'active' : ''}`}
          onClick={onClear}
        >
          {activeFilters.length === 0 && <span className="filter-check">✓</span>}
          <span className="filter-row-label">Toate</span>
        </button>
        <div className="filter-divider" />
        {all.map(([key, cat], i) => (
          <div key={key}>
            <button
              className={`filter-row ${activeFilters.includes(key) ? 'active' : ''}`}
              onClick={() => onToggle(key)}
            >
              {activeFilters.includes(key) && <span className="filter-check">✓</span>}
              <span className="filter-row-label">{cat.label}</span>
            </button>
            {i < all.length - 1 && <div className="filter-divider" />}
          </div>
        ))}
      </div>
    </div>
  );
}

function SkeletonCard() {
  return (
    <div className="card skeleton">
      <div className="skeleton-image" />
      <div className="card-body">
        <div className="skeleton-line short" />
        <div className="skeleton-line" />
        <div className="skeleton-line medium" />
      </div>
    </div>
  );
}

// ─── Admin Panel ─────────────────────────────────────────────────────────────

// Maps the `role` column in the Supabase `admins` table to the display
// labels already used throughout the admin panel's permission checks.
const ROLE_LABELS = { super_admin: 'Super Admin', moderator: 'Moderator', editor: 'Editor' };

const ADMIN_NAV = [
  { id: 'dashboard', label: 'Dashboard', icon: '▦', roles: ['Super Admin', 'Moderator', 'Editor'] },
  { id: 'scrie', label: 'Scrie Articol', icon: '✎', roles: ['Super Admin', 'Editor'] },
  { id: 'sponsorizat', label: 'Articol Sponsorizat', icon: '✎', roles: ['Super Admin', 'Editor'] },
  { id: 'content', label: 'Content Management', icon: '≡', roles: ['Super Admin', 'Moderator'] },
  { id: 'statistici', label: 'Statistici', icon: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>
  ), roles: ['Super Admin'] },
  { id: 'server', label: 'Server', icon: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="8" rx="2"/><rect x="2" y="14" width="20" height="8" rx="2"/><line x1="6" y1="6" x2="6.01" y2="6"/><line x1="6" y1="18" x2="6.01" y2="18"/></svg>
  ), roles: ['Super Admin', 'Moderator'] },
  { id: 'account', label: 'Account', icon: '👤', roles: ['Super Admin'] },
];

function AdminPanel() {
  // Supabase Auth session — null while we don't know yet or aren't logged in.
  const [session, setSession] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  // Role comes from the `admins` table, looked up once we have a session.
  const [role, setRole] = useState(null);
  const [roleLoading, setRoleLoading] = useState(true);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  // "Forgot password" — a small form shown instead of the login form.
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotMsg, setForgotMsg] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  // True right after the user clicks the reset-password link in their
  // email — we show a "set new password" screen instead of the dashboard
  // until they've actually picked a new password.
  const [passwordRecovery, setPasswordRecovery] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirm, setNewPasswordConfirm] = useState('');
  const [newPasswordMsg, setNewPasswordMsg] = useState('');
  const [newPasswordLoading, setNewPasswordLoading] = useState(false);

  const [activePage, setActivePage] = useState(() => sessionStorage.getItem('blitz_admin_page') || 'dashboard');
  const [stats, setStats] = useState(null);
  const [dashboardCustomArticles, setDashboardCustomArticles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [avatar, setAvatar] = useState(() => localStorage.getItem('blitz_admin_avatar') || null);
  const [displayName, setDisplayName] = useState(() => localStorage.getItem('blitz_admin_name') || 'Admin');
  const [topbarMenuOpen, setTopbarMenuOpen] = useState(false);

  // Pick up an existing Supabase session on load, and keep listening for
  // sign-in/sign-out (including from token refresh) so the panel never
  // shows a stale logged-in or logged-out state.
  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setAuthLoading(false);
    });
    const { data: subscription } = supabase.auth.onAuthStateChange((event, newSession) => {
      if (event === 'PASSWORD_RECOVERY') setPasswordRecovery(true);
      setSession(newSession);
    });
    return () => subscription.subscription.unsubscribe();
  }, []);

  // Once we have a session, look up this user's role — this is what
  // actually decides admin access, not just being logged in.
  useEffect(() => {
    if (!session?.user) { setRole(null); setRoleLoading(false); return; }
    setRoleLoading(true);
    fetchAdminRole(session.user.id).then(r => { setRole(r); setRoleLoading(false); });
  }, [session]);

  // Seed a friendlier display name from the account's email the first
  // time they log in, without overwriting a name they've customized since.
  useEffect(() => {
    if (session?.user?.email && !localStorage.getItem('blitz_admin_name')) {
      setDisplayName(session.user.email.split('@')[0]);
    }
  }, [session]);

  const currentRole = ROLE_LABELS[role] || null;

  const canAccess = (pageId) => {
    const navItem = ADMIN_NAV.find(n => n.id === pageId);
    return !navItem || navItem.roles.includes(currentRole);
  };

  // Redirect to dashboard if current page is no longer accessible
  useEffect(() => {
    if (!canAccess(activePage)) setActivePage('dashboard');
  }, [currentRole]);
  const [hiddenIds, setHiddenIds] = useState(new Set());

  useEffect(() => {
    fetchHiddenIds().then(ids => setHiddenIds(ids));
  }, []);

  const avatarInputRef = useRef(null);

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result;
      setAvatar(base64);
      localStorage.setItem('blitz_admin_avatar', base64);
    };
    reader.readAsDataURL(file);
  };

  const toggleHide = async (id) => {
    const next = new Set(hiddenIds);
    if (next.has(id)) {
      next.delete(id);
      await unhideArticle(id);
      await logAction(displayName, `Articol afișat: ${id}`);
    } else {
      next.add(id);
      await hideArticle(id);
      await logAction(displayName, `Articol ascuns: ${id}`);
    }
    setHiddenIds(next);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setLoginLoading(true);
    const { error: signInError } = await supabase.auth.signInWithPassword({
      email: loginEmail,
      password: loginPassword,
    });
    setLoginLoading(false);
    if (signInError) setLoginError('Email sau parolă incorectă.');
    // On success, the onAuthStateChange listener above picks up the new
    // session automatically — no need to set any state here.
  };

  const handleLogout = () => supabase.auth.signOut();

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    const { error } = await supabase.auth.resetPasswordForEmail(forgotEmail, {
      redirectTo: 'https://www.blitz-news.ro/admin',
    });
    setForgotLoading(false);
    // Same message whether or not the email is a real account — this is
    // the standard practice so a login page can't be used to check which
    // emails have admin accounts.
    setForgotMsg(error ? 'A apărut o eroare. Încearcă din nou.' : 'Dacă acest email are cont de admin, a primit un link de resetare a parolei.');
  };

  const handleSetNewPassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) { setNewPasswordMsg('Parola trebuie să aibă cel puțin 6 caractere.'); return; }
    if (newPassword !== newPasswordConfirm) { setNewPasswordMsg('Parolele nu coincid.'); return; }
    setNewPasswordLoading(true);
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    setNewPasswordLoading(false);
    if (error) { setNewPasswordMsg('Eroare: ' + error.message); return; }
    setPasswordRecovery(false);
    setNewPassword(''); setNewPasswordConfirm('');
  };

  useEffect(() => {
    if (!role) return;
    refreshDashboard();
  }, [role]);

  const refreshDashboard = () => {
    setLoading(true);
    Promise.all([
      fetchEditedArticles(),
      fetchCustomArticles(),
    ]).then(([editedArticles, customArticles]) => {
      const editedMap = {};
      editedArticles.forEach(a => { editedMap[a.id] = a; });

      setDashboardCustomArticles(customArticles.filter(a => !a.isDraft));

      // Read from localStorage feed — same source as the website
      try {
        const saved = JSON.parse(localStorage.getItem('blitz_feed') || 'null');
        if (saved?.entries) {
          const data = {};
          SOURCES.forEach(s => { data[s] = []; });
          saved.entries.forEach(e => {
            const article = editedMap[e.article.id]
              ? { ...e.article, ...editedMap[e.article.id] }
              : e.article;
            if (data[article.source]) data[article.source].push(article);
          });
          setStats(data);
        }
      } catch {}
      setLoading(false);
    });
  };

  if (authLoading || (session && roleLoading)) {
    return (
      <div className="admin-login">
        <div className="admin-login-box">
          <div className="admin-logo">{getAppName()} ⚡</div>
          <p style={{color:'var(--text-muted)',fontSize:'13px'}}>Se verifică accesul...</p>
        </div>
      </div>
    );
  }

  if (passwordRecovery) {
    return (
      <div className="admin-login">
        <div className="admin-login-box">
          <div className="admin-logo">{getAppName()} ⚡</div>
          <h1 className="admin-login-title">Setează o parolă nouă</h1>
          <form onSubmit={handleSetNewPassword} style={{display:'flex',flexDirection:'column',gap:'12px'}}>
            <input
              className="admin-input"
              type="password"
              placeholder="Parolă nouă"
              value={newPassword}
              onChange={e => { setNewPassword(e.target.value); setNewPasswordMsg(''); }}
              autoFocus
              autoComplete="new-password"
            />
            <input
              className="admin-input"
              type="password"
              placeholder="Confirmă parola"
              value={newPasswordConfirm}
              onChange={e => { setNewPasswordConfirm(e.target.value); setNewPasswordMsg(''); }}
              autoComplete="new-password"
            />
            {newPasswordMsg && <p className="admin-error">{newPasswordMsg}</p>}
            <button className="admin-btn" type="submit" disabled={newPasswordLoading}>
              {newPasswordLoading ? 'Se salvează...' : 'Salvează parola'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  if (!session) {
    if (forgotMode) {
      return (
        <div className="admin-login">
          <div className="admin-login-box">
            <div className="admin-logo">{getAppName()} ⚡</div>
            <h1 className="admin-login-title">Resetează parola</h1>
            <form onSubmit={handleForgotPassword} style={{display:'flex',flexDirection:'column',gap:'12px'}}>
              <input
                className="admin-input"
                type="email"
                placeholder="Email"
                value={forgotEmail}
                onChange={e => { setForgotEmail(e.target.value); setForgotMsg(''); }}
                autoFocus
                autoComplete="username"
              />
              {forgotMsg && (
                <p className="admin-error" style={forgotMsg.startsWith('Dacă') ? {color:'#22c55e'} : undefined}>{forgotMsg}</p>
              )}
              <button className="admin-btn" type="submit" disabled={forgotLoading}>
                {forgotLoading ? 'Se trimite...' : 'Trimite link de resetare'}
              </button>
            </form>
            <button
              onClick={() => { setForgotMode(false); setForgotMsg(''); }}
              style={{marginTop:'12px',background:'none',border:'none',color:'var(--text-muted)',cursor:'pointer',fontSize:'13px',textAlign:'center'}}
            >
              &larr; Înapoi la login
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="admin-login">
        <div className="admin-login-box">
          <div className="admin-logo">{getAppName()} ⚡</div>
          <h1 className="admin-login-title">Admin Panel</h1>
          <form onSubmit={handleLogin} style={{display:'flex',flexDirection:'column',gap:'12px'}}>
            <input
              className="admin-input"
              type="email"
              placeholder="Email"
              value={loginEmail}
              onChange={e => { setLoginEmail(e.target.value); setLoginError(''); }}
              autoFocus
              autoComplete="username"
            />
            <input
              className={`admin-input ${loginError ? 'admin-input-error' : ''}`}
              type="password"
              placeholder="Parolă"
              value={loginPassword}
              onChange={e => { setLoginPassword(e.target.value); setLoginError(''); }}
              autoComplete="current-password"
            />
            {loginError && <p className="admin-error">{loginError}</p>}
            <button className="admin-btn" type="submit" disabled={loginLoading}>
              {loginLoading ? 'Se conectează...' : 'Intră'}
            </button>
          </form>
          <button
            onClick={() => { setForgotMode(true); setLoginError(''); }}
            style={{marginTop:'12px',background:'none',border:'none',color:'var(--text-muted)',cursor:'pointer',fontSize:'13px',textAlign:'center'}}
          >
            Ai uitat parola?
          </button>
        </div>
      </div>
    );
  }

  if (!role) {
    return (
      <div className="admin-login">
        <div className="admin-login-box">
          <div className="admin-logo">{getAppName()} ⚡</div>
          <h1 className="admin-login-title">Admin Panel</h1>
          <p style={{color:'var(--text-muted)',fontSize:'13px',textAlign:'center'}}>
            Acest cont ({session.user.email}) nu are drepturi de administrator.
          </p>
          <button className="admin-btn" onClick={handleLogout}>Deconectare</button>
        </div>
      </div>
    );
  }

  const totalArticles = stats ? Object.values(stats).reduce((sum, items) => sum + items.length, 0) : 0;
  const sourceCount = SOURCES.length;
  const lastUpdated = new Date().toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' });

  return (
    <div className="admin-shell">
      {/* Sidebar */}
      <aside className={`admin-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="admin-sidebar-logo">{getAppName()} ⚡</div>
        <nav className="admin-nav">
          {ADMIN_NAV.filter(item => item.roles.includes(currentRole)).map(item => (
            <button
              key={item.id}
              className={`admin-nav-item ${activePage === item.id ? 'active' : ''}`}
              onClick={() => { setActivePage(item.id); sessionStorage.setItem('blitz_admin_page', item.id); setSidebarOpen(false); }}
            >
              <span className="admin-nav-icon">{item.icon}</span>
              <span className="admin-nav-label">{item.label}</span>
            </button>
          ))}
        </nav>
        <button className="admin-nav-item admin-nav-logout" onClick={handleLogout}>
          <span className="admin-nav-icon">↩</span>
          <span className="admin-nav-label">Ieșire</span>
        </button>
      </aside>

      {/* Main */}
      <div className="admin-main">
        {/* Top bar */}
        <div className="admin-topbar">
          <button className="admin-hamburger" onClick={() => setSidebarOpen(v => !v)}>
            <span/><span/><span/>
          </button>
          <span className="admin-topbar-title">
            {ADMIN_NAV.find(n => n.id === activePage)?.label}
          </span>
          <span className="admin-topbar-time">Actualizat la {lastUpdated}</span>
          <div style={{position:'relative'}}>
            <div className="admin-topbar-user" onClick={() => setTopbarMenuOpen(v => !v)} style={{cursor:'pointer'}} title="Cont">
              <div className="admin-avatar">
                {avatar ? <img src={avatar} alt="avatar" style={{width:'100%',height:'100%',objectFit:'cover',borderRadius:'50%'}} /> : displayName[0]?.toUpperCase()}
              </div>
              <span className="admin-topbar-username">{displayName}</span>
              {currentRole !== 'Super Admin' && (
                <span style={{fontSize:'10px',fontWeight:'700',padding:'1px 7px',borderRadius:'4px',background: currentRole === 'Moderator' ? 'rgba(245,158,11,0.15)' : 'rgba(34,197,94,0.15)',color: currentRole === 'Moderator' ? '#f59e0b' : '#22c55e'}}>
                  {currentRole}
                </span>
              )}
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color:'var(--text-muted)'}}><polyline points="6 9 12 15 18 9"/></svg>
            </div>
            {topbarMenuOpen && (
              <div className="admin-topbar-dropdown" onClick={e => e.stopPropagation()}>
                <button className="admin-topbar-dd-item" onClick={() => { avatarInputRef.current?.click(); setTopbarMenuOpen(false); }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                  Schimbă avatarul
                </button>
                <button className="admin-topbar-dd-item" onClick={() => { setActivePage('account'); setTopbarMenuOpen(false); }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                  Editează profilul
                </button>
                <div style={{height:'1px',background:'var(--border)',margin:'4px 0'}}/>
                <button className="admin-topbar-dd-item" style={{color:'#e05555'}} onClick={() => { handleLogout(); setTopbarMenuOpen(false); }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
                  Ieșire
                </button>
              </div>
            )}
            <input ref={avatarInputRef} type="file" accept="image/*" style={{display:'none'}} onChange={handleAvatarChange} />
          </div>
        </div>
        {topbarMenuOpen && <div style={{position:'fixed',inset:0,zIndex:101}} onClick={() => setTopbarMenuOpen(false)} />}

        {/* Dashboard page */}
        {activePage === 'dashboard' && (
          <div className="admin-content">
            {/* Stat cards - only 2 left: total + sources */}
            <div className="admin-cards">
              <div className="admin-card">
                <div className="admin-card-label">Total articole</div>
                <div className="admin-card-value">{loading ? '—' : totalArticles}</div>
                <div className="admin-card-sub">din {sourceCount} surse active</div>
              </div>
              <div className="admin-card">
                <div className="admin-card-label">Surse active</div>
                <div className="admin-card-value">{sourceCount}</div>
                <div className="admin-card-sub">Digi24, ProTV, HotNews, Recorder</div>
              </div>
              <div className="admin-card admin-card-placeholder">
                <div className="admin-card-label">Vizite azi</div>
                <div className="admin-card-value admin-card-soon">—</div>
                <div className="admin-card-sub">Disponibil după integrare analytics</div>
              </div>
              <div className="admin-card admin-card-placeholder">
                <div className="admin-card-label">Venituri AdSense</div>
                <div className="admin-card-value admin-card-soon">—</div>
                <div className="admin-card-sub">Disponibil după integrare AdSense</div>
              </div>
            </div>

            {/* Two column layout: feed left, table right */}
            <div className="admin-dashboard-columns">
              {/* Feed preview - left */}
              <div className="admin-feed-section">
                <div className="admin-table-header" style={{display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                  <h2 className="admin-table-title">Feed live</h2>
                  <button onClick={refreshDashboard} title="Reîncarcă feed" style={{background:'none',border:'none',cursor:'pointer',color:'var(--text-muted)',fontSize:'16px',padding:'2px 6px',borderRadius:'6px',lineHeight:1,transition:'color 0.15s'}}
                    onMouseEnter={e=>e.target.style.color='var(--text)'} onMouseLeave={e=>e.target.style.color='var(--text-muted)'}>
                    ↻
                  </button>
                </div>
                <div className="admin-feed-list">
                  {loading ? (
                    <div className="admin-loading">Se încarcă...</div>
                  ) : (
                    [...dashboardCustomArticles, ...Object.values(stats || {}).flat()]
                      .sort((a, b) => {
                        const dateA = a.isCustom && a.updatedAt ? a.updatedAt : a.pubDate;
                        const dateB = b.isCustom && b.updatedAt ? b.updatedAt : b.pubDate;
                        return new Date(dateB) - new Date(dateA);
                      })
                      .slice(0, 20)
                      .map(article => {
                        const isHidden = hiddenIds.has(article.id);
                        const timeAgo = formatTimeAgo(article.isCustom && article.updatedAt ? article.updatedAt : article.pubDate);
                        return (
                          <article className={`card ${isHidden ? 'admin-feed-hidden' : ''}`} key={article.id} style={{cursor:'default'}}>
                            {article.image && (
                              <div className="card-image-wrap">
                                <img src={article.image} alt={article.title} className="card-image" loading="lazy" />
                              </div>
                            )}
                            <div className="card-body">
                              <div className="card-meta">
                                <span className={`source-badge source-${article.source}`}>{article.sourceName}</span>
                                <div className="card-status">
                                  <span className="card-status-time">{timeAgo}</span>
                                  <button className="admin-eye-btn" onClick={() => toggleHide(article.id)} title={isHidden ? 'Arată articol' : 'Ascunde articol'}>
                                    {isHidden ? (
                                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                                    ) : (
                                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                                    )}
                                  </button>
                                  <button className="admin-eye-btn" onClick={() => {
                                    localStorage.setItem('blitz_edit_prefill', JSON.stringify(article));
                                    setActivePage('content');
                                  }} title="Editează card" style={{color:'var(--accent)'}}>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                                  </button>
                                </div>
                              </div>
                              <h2 className="card-title">{article.title}</h2>
                              {article.description && article.description.length > 50 && (
                                <p className="card-desc">{article.description.slice(0, 120)}...</p>
                              )}
                              {isHidden && <span className="admin-feed-hidden-label">Articol ascuns</span>}
                            </div>
                          </article>
                        );
                      })
                  )}
                </div>
              </div>

              {/* Source table - right */}
              <div className="admin-table-section">
                <div className="admin-table-header">
                  <h2 className="admin-table-title">Articole per sursă</h2>
                </div>
                {loading ? (
                  <div className="admin-loading">Se încarcă sursele...</div>
                ) : (
                  <div className="admin-table">
                    <div className="admin-table-row admin-table-head">
                      <span>Sursă</span>
                      <span>Articole</span>
                      <span>Cel mai recent</span>
                      <span>Status</span>
                    </div>
                    {SOURCES.map(source => {
                      const items = stats?.[source] ?? [];
                      const newest = items[0]?.pubDate ? new Date(items[0].pubDate).toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' }) : '—';
                      const ok = items.length > 0;
                      return (
                        <div className="admin-table-row" key={source}>
                          <span className="admin-table-source">{SOURCE_META[source].label}</span>
                          <span className="admin-table-count">{items.length}</span>
                          <span className="admin-table-time">{newest}</span>
                          <span className={`admin-table-status ${ok ? 'ok' : 'err'}`}>
                            {ok ? '● Online' : '● Eroare'}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Placeholder pages */}
        {activePage !== 'dashboard' && activePage !== 'scrie' && activePage !== 'sponsorizat' && activePage !== 'content' && activePage !== 'account' && activePage !== 'server' && activePage !== 'statistici' && (
          <div className="admin-content">
            <div className="admin-placeholder">
              <div className="admin-placeholder-icon">🚧</div>
              <h2>În curând</h2>
              <p>Această secțiune va fi disponibilă în curând.</p>
            </div>
          </div>
        )}

        
        {activePage === 'scrie' && <AdminArticleEditor />}
        {activePage === 'sponsorizat' && <AdminArticleEditor isSponsored />}
        {activePage === 'content' && <AdminContentManagement currentRole={currentRole} displayName={displayName} />}
        {activePage === 'account' && <AdminAccountPage displayName={displayName} setDisplayName={setDisplayName} avatar={avatar} setAvatar={setAvatar} avatarInputRef={avatarInputRef} handleAvatarChange={handleAvatarChange} />}
        {activePage === 'server' && <AdminServerPage />}
        {activePage === 'statistici' && <AdminStatisticiPage />}
      </div>

      {sidebarOpen && <div className="admin-overlay" onClick={() => setSidebarOpen(false)} />}
    </div>
  );
}

function AdminContentManagement({ currentRole = 'Super Admin', displayName = 'Admin' }) {
  const ALL_TABS = ['Editează', 'Ascunse', 'Scrise', 'Sponsorizate', 'Facebook', 'Reclame'];
  const TABS = currentRole === 'Moderator' ? ['Editează', 'Ascunse'] : ALL_TABS;
  const [activeTab, setActiveTab] = useState(() => {
    try {
      const prefill = localStorage.getItem('blitz_edit_prefill');
      if (prefill) return 'Editează';
    } catch {}
    return sessionStorage.getItem('blitz_admin_tab') || 'Ascunse';
  });

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    sessionStorage.setItem('blitz_admin_tab', tab);
    if (tab === 'Facebook') fetchFbQueue();
    setFbClearConfirm(false);
  };

  const _prefill = (() => {
    try {
      const raw = localStorage.getItem('blitz_edit_prefill');
      if (raw) return JSON.parse(raw);
    } catch {}
    return null;
  })();

  const [editQueue, setEditQueue] = useState(() => {
    if (_prefill) {
      localStorage.removeItem('blitz_edit_prefill');
      return [_prefill];
    }
    return [];
  });
  const [editingArticle, setEditingArticle] = useState(() => _prefill || null);
  const [editFields, setEditFields] = useState(() => {
    if (_prefill) {
      return {
        title: _prefill.title || '',
        description: _prefill.description || '',
        image: _prefill.image || '',
        body: _prefill.body || '',
        categories: _prefill.categories ? _prefill.categories.join(', ') : '',
      };
    }
    return {};
  });
  const [savedMsg, setSavedMsg] = useState(false);

  const [hiddenIds, setHiddenIds] = useState(new Set());
  const [customArticles, setCustomArticles] = useState([]);
  const [editedArticles, setEditedArticles] = useState([]);
  const [feedArticles, setFeedArticles] = useState([]);
  const [fbQueue, setFbQueue] = useState([]);
  const [fbLoading, setFbLoading] = useState(false);
  const [fbClearConfirm, setFbClearConfirm] = useState(false);
  const [fbFilter, setFbFilter] = useState('all');
  const [fbFilterOpen, setFbFilterOpen] = useState(false);
  const [fbSort, setFbSort] = useState('desc');
  const [fbSortOpen, setFbSortOpen] = useState(false);
  const [fbSearchOpen, setFbSearchOpen] = useState(false);
  const [fbSearchQuery, setFbSearchQuery] = useState('');

  const [adsEnabled, setAdsEnabled] = useState(true);
  const [adsSaving, setAdsSaving] = useState(false);

  const SUPABASE_URL = 'https://iuekmsrjocpvranvngko.supabase.co';
  const SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Iml1ZWttc3Jqb2NwdnJhbnZuZ2tvIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzM2MDQ1MzgsImV4cCI6MjA4OTE4MDUzOH0.cDL0v7XjjErR-gXVAAMSPNstB4EvypXHDHJsGAmo2YU';

  const fetchFbQueue = async () => {
    setFbLoading(true);
    try {
      const res = await fetch(`${SUPABASE_URL}/rest/v1/facebook_queue?order=pub_date.asc&limit=100&select=*`, {
        headers: { 'apikey': SUPABASE_ANON_KEY, 'Authorization': `Bearer ${SUPABASE_ANON_KEY}` }
      });
      const data = await res.json();
      setFbQueue(Array.isArray(data) ? data : []);
    } catch { setFbQueue([]); }
    setFbLoading(false);
  };

  const clearFbPending = async () => {
    await fetch(`${SUPABASE_URL}/rest/v1/facebook_queue?status=eq.pending`, {
      method: 'DELETE',
      headers: { 'apikey': SUPABASE_ANON_KEY, 'Authorization': `Bearer ${SUPABASE_ANON_KEY}`, 'Prefer': 'return=minimal' }
    });
    setFbClearConfirm(false);
    await fetchFbQueue();
  };

  useEffect(() => {
    fetchHiddenIds().then(ids => setHiddenIds(ids));
    fetchCustomArticles().then(articles => setCustomArticles(articles));
    fetchEditedArticles().then(articles => setEditedArticles(articles));
    fetchAdsEnabledConfig().then(setAdsEnabled);
    // Load feed articles to show hidden ones
    try {
      const saved = JSON.parse(localStorage.getItem('blitz_feed') || 'null');
      if (saved?.entries) setFeedArticles(saved.entries.map(e => e.article));
    } catch {}
    if (sessionStorage.getItem('blitz_admin_tab') === 'Facebook') fetchFbQueue();
  }, []);

  const toggleAds = async () => {
    const next = !adsEnabled;
    setAdsSaving(true);
    setAdsEnabled(next);
    await saveAdsEnabledConfig(next);
    await logAction(displayName, `Reclame ${next ? 'activate' : 'dezactivate'}`);
    setAdsSaving(false);
  };

  const startEdit = (article) => {
    setEditingArticle(article);
    setEditFields({
      title: article.title || '',
      description: article.description || '',
      image: article.image || '',
      categories: article.categories ? article.categories.join(', ') : '',
      body: article.body || '',
    });
  };

  const saveEdit = async () => {
    const isDummy = editingArticle?.id === 'dummy';
    const isSponsored = activeTab === 'Sponsorizate';
    const now = new Date().toISOString();
    const updated = {
      ...(isDummy ? {} : editingArticle),
      id: isDummy ? 'custom_' + Date.now() : editingArticle.id,
      title: editFields.title,
      description: editFields.description,
      image: editFields.image,
      body: editFields.body || editingArticle?.body || '',
      categories: editFields.categories ? editFields.categories.split(',').map(c => c.trim()).filter(Boolean) : null,
      pubDate: isDummy ? now : (editingArticle.pubDate || now), // fixed at creation
      updatedAt: now, // changes on every save
      ...(isDummy ? {
        source: 'blitz',
        sourceName: getAppName(),
        isCustom: true,
        isDraft: false,
        isSponsored: activeTab === 'Sponsorizate',
        originalSource: 'blitz',
      } : {}),
    };

    const isCustomArticle = editingArticle?.isCustom || customArticles.some(a => a.id === editingArticle?.id);

    if (isDummy && (activeTab === 'Scrise' || activeTab === 'Sponsorizate')) {
      // New custom article
      await saveCustomArticle(updated);
      setCustomArticles(prev => [updated, ...prev]);
      setEditingArticle(updated);
    } else if (isCustomArticle) {
      // Existing custom article — update in custom_articles only
      await saveCustomArticle(updated);
      setCustomArticles(prev => prev.map(a => a.id === updated.id ? updated : a));
      setEditingArticle(updated);
    } else {
      // RSS article — save override to edited_articles
      await saveEditedArticle(updated);
      const existing = editedArticles.filter(a => a.id !== updated.id);
      setEditedArticles([updated, ...existing]);
      // Also update in feed cache
      try {
        const saved = JSON.parse(localStorage.getItem('blitz_feed') || 'null');
        if (saved?.entries) {
          saved.entries = saved.entries.map(e => e.article.id === updated.id ? { ...e, article: updated } : e);
          localStorage.setItem('blitz_feed', JSON.stringify(saved));
        }
      } catch {}
    }
    // Remove from edit queue if it was there
    setEditQueue(prev => prev.filter(a => a.id !== updated.id));
    setSavedMsg(true);
    setTimeout(() => { setSavedMsg(false); }, 1500);
  };

  const unhide = async (id) => {
    await unhideArticle(id);
    const next = new Set(hiddenIds);
    next.delete(id);
    setHiddenIds(next);
  };

  const deleteArticle = async (id, type) => {
    if (type === 'hidden') {
      await unhideArticle(id);
      const next = new Set(hiddenIds);
      next.delete(id);
      setHiddenIds(next);
    } else if (type === 'custom') {
      await deleteCustomArticle(id);
      setCustomArticles(prev => prev.filter(a => a.id !== id));
    } else if (type === 'edited') {
      await deleteEditedArticle(id);
      setEditedArticles(prev => prev.filter(a => a.id !== id));
    }
  };

  const hiddenArticles = [...feedArticles, ...customArticles]
    .filter((a, i, arr) => arr.findIndex(x => x.id === a.id) === i)
    .filter(a => hiddenIds.has(a.id))
    .sort((a, b) => new Date(b.pubDate) - new Date(a.pubDate));
  const writtenArticles = customArticles.filter(a => !a.isSponsored).sort((a, b) => new Date(b.pubDate) - new Date(a.pubDate));
  const sponsoredArticles = customArticles.filter(a => a.isSponsored).sort((a, b) => new Date(b.pubDate) - new Date(a.pubDate));

  const getTabArticles = () => {
    if (activeTab === 'Editează') return editQueue;
    if (activeTab === 'Ascunse') return hiddenArticles;
    if (activeTab === 'Scrise') return writtenArticles;
    if (activeTab === 'Sponsorizate') return sponsoredArticles;
    return [];
  };

  const articles = getTabArticles();

  const EyeIcon = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>;
  const PencilIcon = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>;
  const TrashIcon = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/></svg>;

  return (
    <div className="admin-content">
      {/* Tabs */}
      <div className="cm-tabs">
        {TABS.map(tab => (
          <button key={tab} className={`cm-tab ${activeTab === tab ? 'active' : ''}`} onClick={() => handleTabChange(tab)}>
            {tab}
            {tab === 'Editează' && editQueue.length > 0 && <span className="cm-tab-badge">{editQueue.length}</span>}
            {tab === 'Ascunse' && hiddenArticles.length > 0 && <span className="cm-tab-badge">{hiddenArticles.length}</span>}
            {tab === 'Facebook' && fbQueue.filter(r => r.status === 'pending').length > 0 && (
              <span className="cm-tab-badge">{fbQueue.filter(r => r.status === 'pending').length}</span>
            )}
          </button>
        ))}
      </div>

      {/* Facebook tab */}
      {activeTab === 'Facebook' ? (
        <div className="admin-content" style={{padding:'0'}} onClick={() => { setFbFilterOpen(false); setFbSortOpen(false); setFbSearchOpen(false); }}>
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'12px 16px 8px'}}>
            <span style={{fontSize:'13px',color:'var(--text-muted)'}}>
              {fbQueue.length} articole • {fbQueue.filter(r=>r.status==='posted').length} postate • {fbQueue.filter(r=>r.status==='pending').length} în coadă
            </span>
            <div style={{display:'flex',gap:'6px',alignItems:'center'}}>
              {/* Search icon + inline input */}
              <div style={{display:'flex',alignItems:'center',gap:'4px'}} onClick={e => e.stopPropagation()}>
                {fbSearchOpen && (
                  <input
                    autoFocus
                    value={fbSearchQuery}
                    onChange={e => setFbSearchQuery(e.target.value)}
                    placeholder="Caută..."
                    style={{fontSize:'12px',padding:'3px 8px',borderRadius:'6px',border:'1px solid var(--border)',background:'var(--surface)',color:'var(--text)',width:'160px',outline:'none'}}
                  />
                )}
                <button onClick={() => { setFbSearchOpen(v => !v); if (fbSearchOpen) setFbSearchQuery(''); }}
                  style={{background:'none',border:'none',cursor:'pointer',color: fbSearchOpen ? 'var(--accent)' : 'var(--text-muted)',padding:'4px',display:'flex',alignItems:'center'}}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                </button>
              </div>
              {/* Sort — icon only, toggle on click */}
              <button onClick={(e) => { e.stopPropagation(); setFbSort(v => v === 'desc' ? 'asc' : 'desc'); }}
                title={fbSort === 'desc' ? 'Descrescător — click pentru Crescător' : 'Crescător — click pentru Descrescător'}
                style={{background:'none',border:'none',cursor:'pointer',color:'var(--text-muted)',padding:'4px',display:'flex',alignItems:'center'}}>
                {fbSort === 'desc'
                  ? <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="15" y2="12"/><line x1="3" y1="18" x2="9" y2="18"/></svg>
                  : <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="18" x2="21" y2="18"/><line x1="3" y1="12" x2="15" y2="12"/><line x1="3" y1="6" x2="9" y2="6"/></svg>
                }
              </button>
              {/* Filter dropdown */}
              <div style={{position:'relative'}}>
                <button
                  onClick={(e) => { e.stopPropagation(); setFbFilterOpen(v => !v); }}
                  style={{fontSize:'12px',padding:'4px 10px',borderRadius:'6px',border:'1px solid var(--border)',background: fbFilter !== 'all' ? 'rgba(26,108,246,0.15)' : 'transparent',color: fbFilter !== 'all' ? 'var(--accent)' : 'var(--text)',cursor:'pointer',display:'flex',alignItems:'center',gap:'5px'}}
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></svg>
                  {fbFilter === 'all' ? 'Toate' : fbFilter === 'posted' ? 'Postate' : 'În coadă'}
                </button>
                {fbFilterOpen && (
                  <div style={{position:'absolute',right:0,top:'calc(100% + 4px)',background:'var(--surface)',border:'1px solid var(--border)',borderRadius:'8px',minWidth:'120px',zIndex:100,overflow:'hidden',boxShadow:'0 4px 16px rgba(0,0,0,0.2)'}} onClick={e => e.stopPropagation()}>
                    {[['all','Toate'],['posted','Postate'],['pending','În coadă']].map(([val, label]) => (
                      <button key={val} onClick={() => { setFbFilter(val); setFbFilterOpen(false); setFbSearchQuery(''); }}
                        style={{display:'block',width:'100%',textAlign:'left',padding:'8px 14px',fontSize:'13px',border:'none',background: fbFilter === val ? 'rgba(26,108,246,0.12)' : 'transparent',color: fbFilter === val ? 'var(--accent)' : 'var(--text)',cursor:'pointer'}}>
                        {label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {fbClearConfirm ? (
                <>
                  <span style={{fontSize:'12px',color:'var(--text-muted)'}}>Ești sigur?</span>
                  <button onClick={clearFbPending} style={{fontSize:'12px',padding:'4px 10px',borderRadius:'6px',border:'none',background:'#e05555',color:'#fff',cursor:'pointer',fontWeight:'600'}}>Da, șterge</button>
                  <button onClick={() => setFbClearConfirm(false)} style={{fontSize:'12px',padding:'4px 10px',borderRadius:'6px',border:'1px solid var(--border)',background:'transparent',color:'var(--text)',cursor:'pointer'}}>Nu</button>
                </>
              ) : (
                <button onClick={() => setFbClearConfirm(true)} style={{fontSize:'12px',padding:'4px 10px',borderRadius:'6px',border:'1px solid var(--border)',background:'transparent',color:'#e05555',cursor:'pointer'}}>
                  Golește coada
                </button>
              )}
              <button onClick={fetchFbQueue} style={{fontSize:'12px',padding:'4px 10px',borderRadius:'6px',border:'1px solid var(--border)',background:'transparent',color:'var(--text)',cursor:'pointer'}}>
                ↻ Reîncarcă
              </button>
            </div>
          </div>
          {fbLoading ? (
            <div style={{display:'flex',justifyContent:'center',padding:'40px'}}><div className="spinner"/></div>
          ) : fbQueue.length === 0 ? (
            <div className="admin-placeholder" style={{padding:'40px 0'}}>
              <div className="admin-placeholder-icon">📭</div>
              <h2>Coadă goală</h2>
              <p>Nu există articole în coada Facebook.</p>
            </div>
          ) : (
            <div style={{overflowY:'auto', maxHeight:'calc(100vh - 160px)', paddingBottom:'20px'}}>
            <div className="cm-feed">
              {(() => {
                const pendingItems = fbQueue.filter(r => r.status === 'pending');
                const filtered = fbQueue
                  .filter(item => fbFilter === 'all' || item.status === fbFilter)
                  .filter(item => !fbSearchQuery || item.title?.toLowerCase().includes(fbSearchQuery.toLowerCase()))
                  .slice()
                  .sort((a, b) => fbSort === 'desc'
                    ? new Date(b.pub_date) - new Date(a.pub_date)
                    : new Date(a.pub_date) - new Date(b.pub_date)
                  );
                return filtered.map(item => {
                const isPosted = item.status === 'posted';
                const timeAgo = formatTimeAgo(item.pub_date);
                // Estimate time until posted: position in pending queue / 3 articles per run * 15 min
                const pendingIndex = pendingItems.findIndex(r => r.id === item.id);
                const estimatedMinutes = !isPosted && pendingIndex >= 0
                  ? Math.ceil((pendingIndex + 1) / 3) * 15
                  : null;
                const estLabel = estimatedMinutes !== null
                  ? estimatedMinutes < 60
                    ? `~${estimatedMinutes}min`
                    : `~${Math.round(estimatedMinutes/60)}h`
                  : null;
                return (
                  <article key={item.id} className="card cm-card" style={{cursor:'default',position:'relative',overflow:'hidden'}}>
                    {/* Status tint overlay */}
                    <div style={{
                      position:'absolute',inset:0,pointerEvents:'none',zIndex:1,
                      background: isPosted ? 'rgba(34,197,94,0.08)' : 'rgba(245,158,11,0.08)',
                      borderLeft: `3px solid ${isPosted ? '#22c55e' : '#f59e0b'}`,
                    }}/>
                    {/* Status badge top-right */}
                    <div style={{position:'absolute',top:'10px',right:'10px',zIndex:2,display:'flex',flexDirection:'column',alignItems:'flex-end',gap:'4px'}}>
                      {isPosted
                        ? <span style={{fontSize:'11px',fontWeight:'700',padding:'2px 7px',borderRadius:'4px',background:'rgba(34,197,94,0.9)',color:'#fff',display:'flex',alignItems:'center',gap:'4px'}}>✓ Postat</span>
                        : <span style={{fontSize:'11px',fontWeight:'700',padding:'2px 7px',borderRadius:'4px',background:'rgba(245,158,11,0.9)',color:'#fff'}}>⏳ În coadă</span>
                      }
                      {estLabel && (
                        <span style={{fontSize:'10px',fontWeight:'600',padding:'1px 6px',borderRadius:'4px',background:'rgba(0,0,0,0.55)',color:'rgba(255,255,255,0.9)'}}>
                          {estLabel}
                        </span>
                      )}
                    </div>
                    {item.image && (
                      <div className="card-image-wrap">
                        <img src={item.image} alt={item.title} className="card-image" loading="lazy" />
                      </div>
                    )}
                    <div className="card-body" style={{position:'relative',zIndex:1}}>
                      <div className="card-meta">
                        <span className={`source-badge source-${item.source_name?.toLowerCase() || 'blitz'}`}>{item.source_name}</span>
                        <span className="card-status-time">{timeAgo}</span>
                      </div>
                      <h2 className="card-title" style={{paddingRight: item.image ? '0' : '80px'}}>{item.title}</h2>
                      {isPosted && item.posted_at && (
                        <span style={{fontSize:'10px',color:'var(--text-muted)'}}>
                          Postat la {new Date(item.posted_at).toLocaleString('ro-RO',{day:'2-digit',month:'2-digit',hour:'2-digit',minute:'2-digit'})}
                        </span>
                      )}
                    </div>
                  </article>
                );
              });
              })()}
            </div>
            </div>
          )}
        </div>
      ) : activeTab === 'Reclame' ? (
        <div className="admin-content" style={{maxWidth:'520px'}}>
          <div className="admin-card-section">
            <h2 className="admin-table-title">Reclame</h2>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'12px 16px',background:'var(--surface-2)',borderRadius:'10px'}}>
              <div>
                <div style={{fontSize:'14px',fontWeight:'600',color:'var(--text)'}}>Reclame: {adsEnabled ? 'On' : 'Off'}</div>
                <div style={{fontSize:'12px',color:'var(--text-muted)'}}>Activează sau dezactivează afișarea reclamelor în aplicație și pe site.</div>
              </div>
              <button className={`toggle-switch ${adsEnabled ? 'on' : 'off'}`} disabled={adsSaving} onClick={toggleAds}>
                <span className="toggle-knob" />
              </button>
            </div>
          </div>
        </div>
      ) : (
      <div className="cm-layout">
        {/* Feed */}
        <div className="cm-feed">
          {articles.length === 0 ? (
            <div className="admin-placeholder" style={{padding:'40px 0'}}>
              <div className="admin-placeholder-icon">📭</div>
              <h2>Nimic aici</h2>
              <p>Nu există articole în această secțiune.</p>
            </div>
          ) : (
            articles.map(article => {
              const timeAgo = formatTimeAgo(article.pubDate || article.editedAt);
              const isHidden = activeTab === 'Ascunse';
              const isEditing = editingArticle?.id === article.id;
              const displayTitle = isEditing ? (editFields.title || article.title) : article.title;
              const displayDesc = isEditing ? (editFields.description || article.description) : article.description;
              const displayImage = isEditing ? (editFields.image || article.image) : article.image;
              return (
                <article key={article.id} className={`card cm-card ${isHidden ? 'admin-feed-hidden' : ''} ${isEditing ? 'cm-card-editing' : ''}`} style={{cursor:'default'}}>
                  {displayImage && (
                    <div className="card-image-wrap">
                      <img src={displayImage} alt={displayTitle} className="card-image" loading="lazy" />
                    </div>
                  )}
                  <div className="card-body">
                    <div className="card-meta">
                      <span className={`source-badge source-${article.source || 'blitz'}`}>{article.sourceName || 'Blitz'}</span>
                      <div className="card-status">
                        {(activeTab === 'Scrise' || activeTab === 'Sponsorizate') && (
                          <span style={{fontSize:'10px',fontWeight:'700',padding:'1px 6px',borderRadius:'4px',
                            background: article.isDraft ? 'rgba(245,158,11,0.15)' : 'rgba(34,197,94,0.15)',
                            color: article.isDraft ? '#f59e0b' : '#22c55e'
                          }}>{article.isDraft ? 'Draft' : 'Public'}</span>
                        )}
                        <span className="card-status-time">{timeAgo}</span>
                        {activeTab === 'Ascunse' && (
                          <button className="admin-eye-btn" onClick={() => unhide(article.id)} title="Arată în feed">
                            <EyeIcon />
                          </button>
                        )}
                        <button className="admin-eye-btn" onClick={() => startEdit(article)} title="Editează" style={{color: isEditing ? '#22c55e' : 'var(--accent)'}}>
                          <PencilIcon />
                        </button>
                        <button className="admin-eye-btn" onClick={() => {
                          if (activeTab === 'Editează') { setEditQueue(prev => prev.filter(a => a.id !== article.id)); return; }
                          deleteArticle(article.id, activeTab === 'Ascunse' ? 'hidden' : 'custom');
                        }} title="Șterge" style={{color:'#e05555'}}>
                          <TrashIcon />
                        </button>
                      </div>
                    </div>
                    <h2 className="card-title">{displayTitle}</h2>
                    {displayDesc && (isEditing ? displayDesc.length > 0 : displayDesc.length > 50) && (
                      <p className="card-desc">{displayDesc.slice(0, 120)}...</p>
                    )}
                    {isHidden && <span className="admin-feed-hidden-label">Articol ascuns</span>}
                    {activeTab === 'Editate' && article.originalSource && (
                      <span style={{fontSize:'10px',color:'var(--text-muted)',fontStyle:'italic'}}>Sursă originală: {article.originalSource}</span>
                    )}
                  </div>
                </article>
              );
            })
          )}
        </div>

        {/* Edit panel */}
        {editingArticle && articles.length > 0 && (
          <div className="cm-edit-panel">
            <div className="cm-edit-title">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
              Editează card
            </div>

            <div className="cm-edit-field">
              <label className="cm-edit-label">Titlu</label>
              <input className="admin-editor-input" value={editFields.title || ''} onChange={e => setEditFields(p => ({...p, title: e.target.value}))} maxLength={100} />
            </div>
            <div className="cm-edit-field">
              <label className="cm-edit-label">Descriere</label>
              <textarea className="admin-editor-textarea admin-editor-textarea-sm" value={editFields.description || ''} onChange={e => setEditFields(p => ({...p, description: e.target.value}))} maxLength={200} />
            </div>
            {(editingArticle?.isCustom || customArticles.some(a => a.id === editingArticle?.id)) && (
              <div className="cm-edit-field">
                <label className="cm-edit-label">Conținut articol</label>
                <textarea className="admin-editor-textarea admin-editor-textarea-lg" value={editFields.body || ''} onChange={e => setEditFields(p => ({...p, body: e.target.value}))} maxLength={5000} placeholder="Conținutul complet al articolului..." />
              </div>
            )}
            <div className="cm-edit-field">
              <label className="cm-edit-label">URL Imagine</label>
              <input className="admin-editor-input" value={editFields.image || ''} onChange={e => setEditFields(p => ({...p, image: e.target.value}))} />
            </div>
            <div className="cm-edit-field">
              <label className="cm-edit-label">Categorii (separate prin virgulă)</label>
              <input className="admin-editor-input" value={editFields.categories || ''} onChange={e => setEditFields(p => ({...p, categories: e.target.value}))} placeholder="politica, actualitate..." />
            </div>

            <div className="admin-editor-actions">
              <button className={`admin-editor-draft ${savedMsg ? 'saved' : ''}`} onClick={saveEdit}>
                {savedMsg ? '✓ Salvat!' : 'Salvează'}
              </button>
              {editingArticle?.isDraft && (
                <button className="admin-editor-publish" style={{flex:1}} onClick={async () => {
                  const published = { ...editingArticle, ...editFields, isDraft: false, updatedAt: new Date().toISOString(),
                    categories: editFields.categories ? editFields.categories.split(',').map(c => c.trim()).filter(Boolean) : null };
                  await saveCustomArticle(published);
                  setCustomArticles(prev => prev.map(a => a.id === published.id ? published : a));
                  setEditingArticle(published);
                  setSavedMsg(true);
                  setTimeout(() => setSavedMsg(false), 1500);
                }}>Publică</button>
              )}
              <button className="admin-editor-draft" onClick={() => setEditingArticle(null)}>Anulează</button>
            </div>
          </div>
        )}
      </div>
      )}
    </div>
  );
}

function AdminAccountPage({ displayName, setDisplayName, avatar, setAvatar, avatarInputRef, handleAvatarChange }) {
  const [nameInput, setNameInput] = useState(displayName);
  const [nameSaved, setNameSaved] = useState(false);
  const [pwInput, setPwInput] = useState('');
  const [pwConfirm, setPwConfirm] = useState('');
  const [pwMsg, setPwMsg] = useState('');

  const saveName = () => {
    setDisplayName(nameInput);
    localStorage.setItem('blitz_admin_name', nameInput);
    setNameSaved(true);
    setTimeout(() => setNameSaved(false), 2000);
  };

  const savePassword = async () => {
    if (pwInput.length < 6) { setPwMsg('Parola trebuie să aibă cel puțin 6 caractere.'); return; }
    if (pwInput !== pwConfirm) { setPwMsg('Parolele nu coincid.'); return; }
    const { error } = await supabase.auth.updateUser({ password: pwInput });
    if (error) { setPwMsg('Eroare: ' + error.message); return; }
    setPwMsg('Parolă schimbată cu succes!');
    setPwInput(''); setPwConfirm('');
    setTimeout(() => setPwMsg(''), 2000);
  };

  return (
    <div className="admin-content" style={{maxWidth:'760px'}}>

      {/* Profile */}
      <div className="admin-card-section" style={{gap:'20px'}}>
        <h2 className="admin-table-title">Profilul meu</h2>

        {/* Avatar */}
        <div style={{display:'flex',alignItems:'center',gap:'16px'}}>
          <div className="admin-avatar" style={{width:'64px',height:'64px',fontSize:'24px',cursor:'pointer',flexShrink:0}} onClick={() => avatarInputRef.current?.click()}>
            {avatar ? <img src={avatar} alt="avatar" style={{width:'100%',height:'100%',objectFit:'cover',borderRadius:'50%'}} /> : displayName[0]?.toUpperCase()}
          </div>
          <div>
            <div style={{fontSize:'13px',fontWeight:'600',color:'var(--text)',marginBottom:'4px'}}>{displayName}</div>
            <button className="btn-c" style={{fontSize:'12px',padding:'5px 12px'}} onClick={() => avatarInputRef.current?.click()}>Schimbă avatarul</button>
          </div>
        </div>

        {/* Name */}
        <div className="cm-edit-field">
          <label className="cm-edit-label">Nume afișat</label>
          <div style={{display:'flex',gap:'8px'}}>
            <input className="admin-editor-input" value={nameInput} onChange={e => setNameInput(e.target.value)} maxLength={30} style={{flex:1}} />
            <button className={`admin-editor-draft ${nameSaved ? 'saved' : ''}`} style={{width:'120px'}} onClick={saveName}>
              {nameSaved ? '✓ Salvat' : 'Salvează'}
            </button>
          </div>
        </div>

        {/* Password */}
        <div className="cm-edit-field">
          <label className="cm-edit-label">Schimbă parola</label>
          <div style={{display:'flex',flexDirection:'column',gap:'8px'}}>
            <input className="admin-editor-input" type="password" placeholder="Parolă nouă" value={pwInput} onChange={e => setPwInput(e.target.value)} />
            <input className="admin-editor-input" type="password" placeholder="Confirmă parola" value={pwConfirm} onChange={e => setPwConfirm(e.target.value)} />
            {pwMsg && <span style={{fontSize:'12px',color: pwMsg.includes('succes') ? '#22c55e' : '#e05555'}}>{pwMsg}</span>}
            <button className="admin-editor-publish" style={{alignSelf:'flex-start',padding:'8px 20px',fontSize:'13px'}} onClick={savePassword}>Schimbă parola</button>
          </div>
        </div>
      </div>

      {/* Audit Log */}
      <div className="admin-card-section">
        <h2 className="admin-table-title">Jurnal de activitate</h2>
        <AuditLogSection />
      </div>
    </div>
  );
}

function AuditLogSection() {
  const [log, setLog] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAuditLog().then(data => { setLog(data); setLoading(false); });
  }, []);

  const handleClear = async () => {
    await clearAuditLog();
    setLog([]);
  };

  if (loading) return <p style={{fontSize:'13px',color:'var(--text-muted)'}}>Se încarcă...</p>;

  return (
    <>
      {log.length === 0 ? (
        <p style={{fontSize:'13px',color:'var(--text-muted)'}}>Nu există activitate înregistrată.</p>
      ) : (
        <div style={{display:'flex',flexDirection:'column',gap:'6px',maxHeight:'300px',overflowY:'auto'}}>
          {log.map(entry => (
            <div key={entry.id} style={{display:'flex',gap:'12px',padding:'8px 12px',background:'var(--surface-2)',borderRadius:'8px',fontSize:'12px'}}>
              <span style={{color:'var(--text-muted)',flexShrink:0}}>{new Date(entry.created_at).toLocaleString('ro-RO',{day:'2-digit',month:'2-digit',hour:'2-digit',minute:'2-digit'})}</span>
              <span style={{color:'var(--accent)',flexShrink:0,fontWeight:'600'}}>{entry.user_name}</span>
              <span style={{color:'var(--text)'}}>{entry.action}</span>
            </div>
          ))}
        </div>
      )}
      <button className="btn-c" style={{alignSelf:'flex-start',fontSize:'12px'}} onClick={handleClear}>Golește jurnalul</button>
    </>
  );
}

function AdminServerPage() {
  const [webhookUrl, setWebhookUrl] = useState(() => localStorage.getItem('blitz_webhook_url') || '');
  const [webhookSaved, setWebhookSaved] = useState(false);
  const [maintenanceMode, setMaintenanceMode] = useState(() => {
    try { return JSON.parse(localStorage.getItem('blitz_maintenance') || 'false'); } catch { return false; }
  });
  const [maintenanceMsg, setMaintenanceMsg] = useState(() => localStorage.getItem('blitz_maintenance_msg') || '');
  const [appName, setAppName] = useState(() => localStorage.getItem('blitz_app_name') || 'Blitz');
  const [appDesc, setAppDesc] = useState(() => localStorage.getItem('blitz_app_desc') || 'Știri din România, rapid');
  const [settingsSaved, setSettingsSaved] = useState(false);

  const saveWebhook = () => {
    localStorage.setItem('blitz_webhook_url', webhookUrl);
    setWebhookSaved(true);
    setTimeout(() => setWebhookSaved(false), 2000);
  };

  const toggleMaintenance = () => {
    const next = !maintenanceMode;
    setMaintenanceMode(next);
    localStorage.setItem('blitz_maintenance', JSON.stringify(next));
  };

  const saveSettings = () => {
    localStorage.setItem('blitz_app_name', appName);
    localStorage.setItem('blitz_app_desc', appDesc);
    localStorage.setItem('blitz_maintenance_msg', maintenanceMsg);
    setSettingsSaved(true);
    setTimeout(() => setSettingsSaved(false), 2000);
  };

  const handleBackup = async () => {
    const [hiddenIds, customArticles, editedArticles, auditLog] = await Promise.all([
      fetchHiddenIds(),
      fetchCustomArticles(),
      fetchEditedArticles(),
      fetchAuditLog(),
    ]);
    const backup = {
      exportedAt: new Date().toISOString(),
      hiddenIds: [...hiddenIds],
      customArticles,
      editedArticles,
      auditLog,
    };
    const blob = new Blob([JSON.stringify(backup, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `blitz-backup-${new Date().toISOString().slice(0,10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="admin-content" style={{maxWidth:'760px'}}>

      {/* App Settings */}
      <div className="admin-card-section">
        <h2 className="admin-table-title">Setări aplicație</h2>
        <div className="cm-edit-field">
          <label className="cm-edit-label">Nume aplicație</label>
          <input className="admin-editor-input" value={appName} onChange={e => setAppName(e.target.value)} maxLength={30} />
        </div>
        <div className="cm-edit-field">
          <label className="cm-edit-label">Descriere</label>
          <input className="admin-editor-input" value={appDesc} onChange={e => setAppDesc(e.target.value)} maxLength={100} />
        </div>
        <div style={{display:'flex',flexDirection:'column',gap:'10px',padding:'12px 16px',background:'var(--surface-2)',borderRadius:'10px'}}>
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between'}}>
            <div>
              <div style={{fontSize:'14px',fontWeight:'600',color:'var(--text)'}}>Mod mentenanță</div>
              <div style={{fontSize:'12px',color:'var(--text-muted)'}}>Afișează un mesaj de mentenanță utilizatorilor</div>
            </div>
            <button className={`toggle-switch ${maintenanceMode ? 'on' : 'off'}`} onClick={toggleMaintenance}>
              <span className="toggle-knob" />
            </button>
          </div>
          {maintenanceMode && (
            <textarea
              className="admin-editor-textarea admin-editor-textarea-sm"
              placeholder={`${appName || 'Blitz'} este momentan în mentenanță. Revenim în curând! ⚡`}
              value={maintenanceMsg}
              onChange={e => setMaintenanceMsg(e.target.value)}
              maxLength={300}
              style={{marginTop:'4px'}}
            />
          )}
        </div>
        <button className={`admin-editor-draft ${settingsSaved ? 'saved' : ''}`} style={{alignSelf:'flex-start',padding:'8px 20px',fontSize:'13px'}} onClick={saveSettings}>
          {settingsSaved ? '✓ Salvat' : 'Salvează setările'}
        </button>
      </div>

      {/* Notifications */}
      <div className="admin-card-section">
        <h2 className="admin-table-title">Notificări sistem</h2>
        <p style={{fontSize:'13px',color:'var(--text-muted)'}}>Primește o notificare webhook când o sursă RSS este offline.</p>
        <div className="cm-edit-field">
          <label className="cm-edit-label">Webhook URL (Discord, Slack, etc.)</label>
          <div style={{display:'flex',gap:'8px'}}>
            <input className="admin-editor-input" placeholder="https://hooks.slack.com/..." value={webhookUrl} onChange={e => setWebhookUrl(e.target.value)} style={{flex:1}} />
            <button className={`admin-editor-draft ${webhookSaved ? 'saved' : ''}`} style={{width:'120px'}} onClick={saveWebhook}>
              {webhookSaved ? '✓ Salvat' : 'Salvează'}
            </button>
          </div>
        </div>
      </div>

      {/* Backup */}
      <div className="admin-card-section">
        <h2 className="admin-table-title">Backup date</h2>
        <p style={{fontSize:'13px',color:'var(--text-muted)'}}>Exportă toate articolele ascunse, scrise, editate și conturile ca fișier JSON.</p>
        <button className="admin-editor-publish" style={{alignSelf:'flex-start',padding:'8px 20px',fontSize:'13px',display:'flex',alignItems:'center',gap:'8px'}} onClick={handleBackup}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
          Descarcă backup
        </button>
      </div>
    </div>
  );
}

function AdminStatisticiPage() {
  return (
    <div className="admin-content" style={{maxWidth:'760px'}}>
      <div className="admin-card-section">
        <h2 className="admin-table-title" style={{marginBottom:'12px'}}>Statistici</h2>
        <p style={{fontSize:'13px',color:'var(--text-muted)'}}>Disponibil după integrarea Google Analytics sau a unui serviciu de analytics propriu.</p>
      </div>
    </div>
  );
}

function AdminArticleEditor({ isSponsored = false }) {
  const TITLE_MAX = 100;
  const DESC_MAX = 200;
  const BODY_MAX = 5000;

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [body, setBody] = useState('');
  const [image, setImage] = useState('');
  const [link, setLink] = useState('');
  const [saved, setSaved] = useState(false);
  const [confirmPublish, setConfirmPublish] = useState(false);
  const [published, setPublished] = useState(false);

  const handleSaveDraft = async () => {
    const now = new Date().toISOString();
    const article = {
      id: 'custom_' + Date.now(),
      title, description, body, image, link,
      source: 'blitz', sourceName: getAppName(),
      pubDate: now, updatedAt: now,
      categories: null, isCustom: true, isDraft: true, isSponsored,
    };
    await saveCustomArticle(article);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const handlePublish = async () => {
    const now = new Date().toISOString();
    const article = {
      id: 'custom_' + Date.now(),
      title, description, body, image, link,
      source: 'blitz', sourceName: getAppName(),
      pubDate: now, updatedAt: now,
      categories: null, isCustom: true, isDraft: false, isSponsored,
    };
    await saveCustomArticle(article);
    setConfirmPublish(false);
    setPublished(true);
    setTimeout(() => setPublished(false), 2000);
  };

  const canSave = title.trim().length > 0 && description.trim().length > 0 && body.trim().length > 0;

  return (
    <div className="admin-content">
      <div className="admin-editor-layout">
        {/* Editor */}
        <div className="admin-editor">
          <div className="admin-editor-section">
            <div className="admin-editor-label-row">
              <label className="admin-editor-label">Titlu</label>
              <span className={`admin-editor-counter ${title.length > TITLE_MAX * 0.9 ? 'warn' : ''}`}>{title.length}/{TITLE_MAX}</span>
            </div>
            <input
              className="admin-editor-input"
              type="text"
              placeholder="Titlul articolului..."
              value={title}
              maxLength={TITLE_MAX}
              onChange={e => setTitle(e.target.value)}
            />
          </div>

          <div className="admin-editor-section">
            <div className="admin-editor-label-row">
              <label className="admin-editor-label">Descriere (preview în feed)</label>
              <span className={`admin-editor-counter ${description.length > DESC_MAX * 0.9 ? 'warn' : ''}`}>{description.length}/{DESC_MAX}</span>
            </div>
            <textarea
              className="admin-editor-textarea admin-editor-textarea-sm"
              placeholder="Scurtă descriere care apare sub titlu în feed..."
              value={description}
              maxLength={DESC_MAX}
              onChange={e => setDescription(e.target.value)}
            />
          </div>

          <div className="admin-editor-section">
            <div className="admin-editor-label-row">
              <label className="admin-editor-label">Conținut articol</label>
              <span className={`admin-editor-counter ${body.length > BODY_MAX * 0.9 ? 'warn' : ''}`}>{body.length}/{BODY_MAX}</span>
            </div>
            <textarea
              className="admin-editor-textarea admin-editor-textarea-lg"
              placeholder="Conținutul complet al articolului..."
              value={body}
              maxLength={BODY_MAX}
              onChange={e => setBody(e.target.value)}
            />
          </div>

          <div className="admin-editor-section">
            <label className="admin-editor-label">URL Imagine (opțional)</label>
            <input
              className="admin-editor-input"
              type="text"
              placeholder="https://..."
              value={image}
              onChange={e => setImage(e.target.value)}
            />
          </div>

          <div className="admin-editor-section">
            <label className="admin-editor-label">Link articol (opțional)</label>
            <input
              className="admin-editor-input"
              type="text"
              placeholder="https://..."
              value={link}
              onChange={e => setLink(e.target.value)}
            />
          </div>

          <div className="admin-editor-actions">
            <button
              className={`admin-editor-draft ${!canSave ? 'disabled' : ''} ${saved ? 'saved' : ''}`}
              onClick={handleSaveDraft}
              disabled={!canSave}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
              {saved ? 'Salvat!' : 'Salvează ciorna'}
            </button>
            <button
              className={`admin-editor-publish ${!canSave ? 'disabled' : ''} ${published ? 'saved' : ''}`}
              onClick={() => canSave && setConfirmPublish(true)}
              disabled={!canSave}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="16 16 12 12 8 16"/><line x1="12" y1="12" x2="12" y2="21"/><path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3"/></svg>
              {published ? 'Publicat!' : 'Publică articolul'}
            </button>
          </div>

          {confirmPublish && (
            <div className="admin-confirm">
              <p className="admin-confirm-text">⚠️ Ești pe punctul de a publica un articol. Ești sigur?</p>
              <div className="admin-confirm-actions">
                <button className="admin-confirm-yes" onClick={handlePublish}>Da, publică</button>
                <button className="admin-confirm-no" onClick={() => setConfirmPublish(false)}>Nu, anulează</button>
              </div>
            </div>
          )}
        </div>

        {/* Preview */}
        <div className="admin-editor-preview">
          <h3 className="admin-editor-preview-title">Preview card</h3>
          <article className="card" style={{cursor:'default', maxWidth:'480px'}}>
            {image && (
              <div className="card-image-wrap">
                <img src={image} alt={title} className="card-image" />
              </div>
            )}
            <div className="card-body">
              <div className="card-meta">
                <div style={{display:'flex',alignItems:'center',gap:'6px'}}>
                  <span className="source-badge" style={{background:'rgba(26,108,246,0.2)', color:'var(--accent)'}}>Blitz</span>
                  {isSponsored && <span style={{fontSize:'10px',fontWeight:'700',padding:'2px 7px',borderRadius:'4px',background:'rgba(245,158,11,0.15)',color:'#f59e0b',textTransform:'uppercase',letterSpacing:'0.4px'}}>Sponsorizat</span>}
                </div>
                <span className="card-status-time">acum</span>
              </div>
              <h2 className="card-title">{title || 'Titlul articolului apare aici'}</h2>
              {description && (
                <p className="card-desc">{description.slice(0, 120)}{description.length > 120 ? '...' : ''}</p>
              )}
            </div>
          </article>
        </div>
      </div>
    </div>
  );
}



export default function App() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPage, setMenuPage] = useState('main');
  const [pushAlertsOn, setPushAlertsOn] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('blitz_push_alerts') ?? 'false');
      // If saved as true but permission was later denied, reset to false
      if (saved && Notification.permission === 'denied') return false;
      return saved;
    } catch { return false; }
  });
  const [bellVisible, setBellVisible] = useState(() => {
    try { return JSON.parse(localStorage.getItem('blitz_bell_visible') ?? 'true'); } catch { return true; }
  });
  const [showFeedImages, setShowFeedImages] = useState(() => {
    try { return JSON.parse(localStorage.getItem('blitz_feed_images') ?? 'true'); } catch { return true; }
  });
  const [showArticleImages, setShowArticleImages] = useState(() => {
    try { return JSON.parse(localStorage.getItem('blitz_article_images') ?? 'true'); } catch { return true; }
  });
  const [visibleCount, setVisibleCount] = useState(30);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [activeFilters, setActiveFilters] = useState([]);
  const [darkMode, setDarkMode] = useState(() => {
    try { return JSON.parse(localStorage.getItem('blitz_dark_mode') ?? 'true'); } catch { return true; }
  });
  const [enabledSources, setEnabledSources] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('blitz_enabled_sources'));
      if (Array.isArray(saved)) return saved;
    } catch {}
    return [...SOURCES]; // all on by default
  });
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [readIds, setReadIds] = useState(() => getReadIds());
  const [openShareId, setOpenShareId] = useState(null);

  useEffect(() => {
    const close = () => setOpenShareId(null);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, []);

  // Refresh the cached ads on/off setting so any future ad component reads
  // an up-to-date value via getAdsEnabled().
  useEffect(() => { refreshAdsEnabled(); }, []);

  // seenIds: { articleId: fetchedAt (timestamp) }
  const seenIdsRef = useRef({});
  const pushAlertsRef = useRef(pushAlertsOn);
  useEffect(() => { pushAlertsRef.current = pushAlertsOn; }, [pushAlertsOn]);

  useEffect(() => {
    document.body.setAttribute('data-theme', darkMode ? 'dark' : 'light');
    localStorage.setItem('blitz_dark_mode', JSON.stringify(darkMode));
  }, [darkMode]);

  useEffect(() => {
    localStorage.setItem('blitz_push_alerts', JSON.stringify(pushAlertsOn));
  }, [pushAlertsOn]);

  useEffect(() => {
    localStorage.setItem('blitz_bell_visible', JSON.stringify(bellVisible));
  }, [bellVisible]);

  useEffect(() => {
    localStorage.setItem('blitz_feed_images', JSON.stringify(showFeedImages));
  }, [showFeedImages]);

  useEffect(() => {
    localStorage.setItem('blitz_article_images', JSON.stringify(showArticleImages));
  }, [showArticleImages]);

  useEffect(() => {
    localStorage.setItem('blitz_enabled_sources', JSON.stringify(enabledSources));
  }, [enabledSources]);

  const handleOpenArticle = useCallback((entry) => {
    markAsRead(entry.article.id);
    setReadIds(getReadIds());
    setSelected(entry);
    window.history.pushState({ article: true }, '');
  }, []);

  useEffect(() => {
    const handlePopState = () => setSelected(null);
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Someone opened a shared blitz-news.ro/s/<code> link — that redirects
  // here with ?a=<code>. Fetch the stored snapshot and open it directly,
  // then clean the URL so a refresh doesn't keep re-opening it.
  useEffect(() => {
    const code = new URLSearchParams(window.location.search).get('a');
    if (!code) return;
    window.history.replaceState({}, '', window.location.pathname);
    fetchSharedArticle(code).then(article => {
      if (article) handleOpenArticle({ article, alsoOn: [] });
    });
  }, [handleOpenArticle]);

  // Android hardware back button / edge-swipe: close whatever's open
  // instead of exiting the app, only exiting once nothing is left to close.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    let listenerHandle;
    CapacitorApp.addListener('backButton', () => {
      if (openShareId) { setOpenShareId(null); return; }
      if (searchOpen) { setSearchOpen(false); setSearchQuery(''); return; }
      if (filterOpen) { setFilterOpen(false); return; }
      if (menuOpen) {
        if (menuPage !== 'main') setMenuPage('main');
        else setMenuOpen(false);
        return;
      }
      if (selected) { window.history.back(); return; }
      CapacitorApp.exitApp();
    }).then(handle => { listenerHandle = handle; });
    return () => { listenerHandle && listenerHandle.remove(); };
  }, [openShareId, searchOpen, filterOpen, menuOpen, menuPage, selected]);

  const fetchNews = useCallback(async (isRefresh = false, retryCount = 0) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    setError(null);
    const MAX_RETRIES = 3;
    const RETRY_DELAY = 2000;

    try {
      const results = await Promise.allSettled(
        SOURCES.map(source =>
          fetch(`${API_BASE}/api/rss?source=${source}`).then(r => r.json())
        )
      );

      const allArticles = [];
      for (const result of results) {
        if (result.status === 'fulfilled' && result.value.items) {
          allArticles.push(...result.value.items);
        }
      }

      if (allArticles.length === 0) {
        if (!isRefresh) setError('Nu s-au putut incarca stirile. Incearca din nou.');
        return;
      }

      const fetchedAt = Date.now();
      const currentSeenIds = seenIdsRef.current;

      // Expire seenIds older than 12h so articles can re-surface after cache clears
      const freshSeenIds = {};
      for (const [id, ts] of Object.entries(currentSeenIds)) {
        if (fetchedAt - ts < EXPIRY_MS) freshSeenIds[id] = ts;
      }

      // Separate truly new articles from updated ones (same URL = same article)
      const newArticles = allArticles.filter(a => !freshSeenIds[a.id]);
      const updatedArticles = allArticles.filter(a => freshSeenIds[a.id]);

      if (newArticles.length === 0 && isRefresh) {
        // Nothing new — silently update title + description in-place
        setEntries(prev => prev.map(entry => {
          const updated = updatedArticles.find(a => a.id === entry.article.id);
          if (updated) return { ...entry, article: { ...entry.article, title: updated.title, description: updated.description } };
          return entry;
        }));
        setLastUpdated(new Date());
        return;
      }

      // Same-source title dedup: if two articles from same source have very similar titles, keep only the first
      const normalizeTitle = t => t.toLowerCase().replace(/[^a-z0-9]/g, '').slice(0, 50);
      const seenSourceTitles = new Set();
      const dedupedNewArticles = newArticles.filter(a => {
        const key = `${a.source}:${normalizeTitle(a.title)}`;
        if (seenSourceTitles.has(key)) return false;
        seenSourceTitles.add(key);
        return true;
      });

      // Sort chronologically (newest first)
      dedupedNewArticles.sort((a, b) => new Date(b.pubDate) - new Date(a.pubDate));

      // Fire push notification if enabled
      if (isRefresh && pushAlertsRef.current && Notification.permission === 'granted') {
        new Notification(`${getAppName()} ⚡`, {
          body: dedupedNewArticles[0].title,
          icon: '/favicon.ico',
        });
      }

      // Assign fetch timestamp to new articles only
      const newSeenIds = { ...freshSeenIds };
      for (const a of dedupedNewArticles) {
        newSeenIds[a.id] = fetchedAt;
      }
      seenIdsRef.current = newSeenIds;

      // Merge all articles and sort chronologically (newest first)
      setEntries(prev => {
        // Build set of all current RSS IDs — articles no longer in RSS get removed
        const currentRssIds = new Set(allArticles.map(a => a.id));

        const updatedPrev = prev
          .filter(e => {
            // Keep custom/blitz articles always
            if (e.article.source === 'blitz' || e.article.isCustom) return true;
            // Remove RSS articles that are no longer in the current feed
            return currentRssIds.has(e.article.id);
          })
          .map(entry => {
            const updated = updatedArticles.find(a => a.id === entry.article.id);
            if (updated) return { ...entry, article: { ...entry.article, title: updated.title, description: updated.description } };
            return entry;
          });
        const existingIds = new Set(updatedPrev.map(e => e.article.id));
        // Build description map to catch same-story/different-title duplicates from same source
        const existingDescs = new Map();
        updatedPrev.forEach(e => {
          if (e.article.description && e.article.description.length > 30) {
            const key = `${e.article.source}:${e.article.description.slice(0, 80).toLowerCase().replace(/\s+/g, ' ')}`;
            existingDescs.set(key, true);
          }
        });
        const newEntries = dedupedNewArticles
          .filter(a => {
            if (existingIds.has(a.id)) return false;
            if (a.description && a.description.length > 30) {
              const key = `${a.source}:${a.description.slice(0, 80).toLowerCase().replace(/\s+/g, ' ')}`;
              if (existingDescs.has(key)) return false;
            }
            return true;
          })
          .map(a => ({ article: a, alsoOn: [] }));
        const merged = [...newEntries, ...updatedPrev];
        merged.sort((a, b) => new Date(b.article.pubDate) - new Date(a.article.pubDate));
        const capped = merged.slice(0, 200);
        saveFeed(capped, newSeenIds);
        return capped;
      });
      setLastUpdated(new Date());

    } catch (err) {
      if (retryCount < MAX_RETRIES) {
        setTimeout(() => fetchNews(isRefresh, retryCount + 1), RETRY_DELAY * (retryCount + 1));
        return;
      }
      if (!isRefresh) setError('Eroare la incarcarea stirilor.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  // On mount: load from localStorage first, then fetch fresh
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const saved = loadFeed();
    if (saved && saved.entries && saved.entries.length > 0) {
      setEntries(saved.entries);
      // Use saved seenIds, but also ensure all cached entry IDs are marked as seen
      const seenIds = saved.seenIds || {};
      const now = Date.now();
      saved.entries.forEach(e => {
        if (!seenIds[e.article.id]) seenIds[e.article.id] = now - 1000;
      });
      seenIdsRef.current = seenIds;
      setLoading(false);
      // Still fetch in background to check for new articles
      fetchNews(true);
    } else {
      fetchNews(false);
    }

    const interval = setInterval(() => fetchNews(true), 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchNews]);

  useEffect(() => { setVisibleCount(30); }, [activeFilters, enabledSources, searchQuery]);

  useEffect(() => {
    const handleScroll = () => {
      if (window.innerHeight + window.scrollY >= document.body.offsetHeight - 300) {
        setVisibleCount(prev => prev + 20);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Pull-to-refresh: only active on the main feed, and only once we're
  // scrolled all the way to the top. Uses native touch listeners (not React's
  // onTouch* props) because we need { passive: false } to call
  // preventDefault() and stop the page/browser from scrolling while dragging.
  const [pullDistance, setPullDistance] = useState(0);
  const [isPulling, setIsPulling] = useState(false);
  const touchStartYRef = useRef(0);
  const pullActiveRef = useRef(false);
  const pullDistanceRef = useRef(0);
  const PULL_TRIGGER = 64; // px of visual pull needed to fire a refresh
  const PULL_MAX = 90; // visual cap, so it doesn't stretch forever

  useEffect(() => {
    // Pausing on any overlay avoids the gesture fighting with menu/search/filter scrolling.
    if (selected || menuOpen || filterOpen || searchOpen) return;

    const handleTouchStart = (e) => {
      if (window.scrollY > 0) return;
      touchStartYRef.current = e.touches[0].clientY;
      pullActiveRef.current = true;
      setIsPulling(true);
    };

    const handleTouchMove = (e) => {
      if (!pullActiveRef.current) return;
      const rawDelta = e.touches[0].clientY - touchStartYRef.current;
      if (rawDelta <= 0 || window.scrollY > 0) {
        pullActiveRef.current = false;
        pullDistanceRef.current = 0;
        setPullDistance(0);
        return;
      }
      // Rubber-band resistance: the further you pull, the less it moves.
      const visual = Math.min(rawDelta * 0.5, PULL_MAX);
      pullDistanceRef.current = visual;
      setPullDistance(visual);
      e.preventDefault();
    };

    const handleTouchEnd = () => {
      if (!pullActiveRef.current) return;
      pullActiveRef.current = false;
      setIsPulling(false);
      if (pullDistanceRef.current >= PULL_TRIGGER) {
        setPullDistance(48); // hold the indicator open at spinner height
        fetchNews(true).finally(() => setPullDistance(0));
      } else {
        setPullDistance(0);
      }
      pullDistanceRef.current = 0;
    };

    window.addEventListener('touchstart', handleTouchStart, { passive: true });
    window.addEventListener('touchmove', handleTouchMove, { passive: false });
    window.addEventListener('touchend', handleTouchEnd, { passive: true });
    return () => {
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [selected, menuOpen, filterOpen, searchOpen, fetchNews]);

  // Chrome's own pull-to-refresh (reloads the whole page) would otherwise fire
  // alongside ours. `overscroll-behavior: none` in App.css already blocks that
  // in the regular mobile browser; inside the Capacitor app we relax it to
  // "contain" so the WebView still scrolls/bounces normally at its edges.
  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      document.body.style.overscrollBehaviorY = 'contain';
    }
  }, []);

  const [sbHiddenIds, setSbHiddenIds] = useState(new Set());
  const [sbEditedMap, setSbEditedMap] = useState({});
  const [sbCustomArticles, setSbCustomArticles] = useState([]);

  useEffect(() => {
    fetchHiddenIds().then(ids => setSbHiddenIds(ids));
    fetchEditedArticles().then(articles => {
      const map = {};
      articles.forEach(a => { map[a.id] = a; });
      setSbEditedMap(map);
    });
    fetchCustomArticles().then(articles => setSbCustomArticles(articles.filter(a => !a.isDraft)));
  }, []);

  const dedupedEntries = useMemo(() => {
    const allEntries = [
      ...sbCustomArticles.map(a => ({ article: a, alsoOn: [] })),
      ...entries.map(e => ({
        ...e,
        article: sbEditedMap[e.article.id] ? { ...e.article, ...sbEditedMap[e.article.id] } : e.article,
      })),
    ]
    .filter(e => !sbHiddenIds.has(e.article.id))
    .sort((a, b) => {
      const dateA = a.article.isCustom && a.article.updatedAt ? a.article.updatedAt : a.article.pubDate;
      const dateB = b.article.isCustom && b.article.updatedAt ? b.article.updatedAt : b.article.pubDate;
      return new Date(dateB) - new Date(dateA);
    });

    const sourceFiltered = allEntries.filter(e => e.article.source === 'blitz' || enabledSources.includes(e.article.source));
    const groups = deduplicateArticles(sourceFiltered.map(e => e.article));
    return resolveGroups(groups, [...enabledSources, 'blitz']);
  }, [entries, enabledSources, sbHiddenIds, sbEditedMap, sbCustomArticles]);

  if (window.location.pathname === '/admin') {
    return <AdminPanel />;
  }

  // Maintenance mode check
  const isMaintenance = (() => { try { return JSON.parse(localStorage.getItem('blitz_maintenance') || 'false'); } catch { return false; } })();
  if (isMaintenance) {
    const msg = localStorage.getItem('blitz_maintenance_msg') || `${getAppName()} este momentan în mentenanță. Revenim în curând! ⚡`;
    return (
      <div style={{minHeight:'100vh',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',padding:'32px',textAlign:'center',gap:'16px'}}>
        <span style={{fontSize:'48px'}}>⚡</span>
        <h1 style={{fontSize:'24px',fontWeight:'700',color:'var(--text)',fontFamily:"'Syne',sans-serif"}}>{getAppName()}</h1>
        <p style={{fontSize:'16px',color:'var(--text-muted)',maxWidth:'360px',lineHeight:'1.6'}}>{msg}</p>
      </div>
    );
  }

  if (selected) {
    return (
      <div className="app">
        <ArticleReader entry={selected} onBack={() => window.history.back()} showImage={showArticleImages} openShareId={openShareId} setOpenShareId={setOpenShareId} />
      </div>
    );
  }

  return (
    <div className="app">
      <header className="header">
        <div className="header-inner">
          <div className="logo">
            <button className="hamburger-btn" aria-label="Menu" onClick={() => setMenuOpen(true)}>
              <span /><span /><span />
            </button>
            <button className="logo-name" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>
              <span className="logo-text">{getAppName()}</span>
            </button>
          </div>
          <div className="header-actions">
            {(() => {
              const lastReadIndex = entries.findIndex(e => readIds[e.article.id]);
              const count = lastReadIndex > 0
                ? entries.slice(0, lastReadIndex).filter(e => !readIds[e.article.id]).length
                : 0;
              return count > 0 && bellVisible ? (
                <span className="unread-btn">&#128276; {count}</span>
              ) : null;
            })()}
          </div>
        </div>
        <div className="header-bottom">
          {lastUpdated && (
            <span className="last-updated">
              Actualizat la {lastUpdated.toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
          <button className="filter-btn" onClick={() => setFilterOpen(!filterOpen)} aria-label="Filtre">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line x1="3" y1="6" x2="21" y2="6" />
              <circle cx="15" cy="6" r="3" fill="var(--bg)" />
              <line x1="3" y1="12" x2="21" y2="12" />
              <circle cx="8" cy="12" r="3" fill="var(--bg)" />
              <line x1="3" y1="18" x2="21" y2="18" />
              <circle cx="17" cy="18" r="3" fill="var(--bg)" />
            </svg>
          </button>
        </div>
      </header>

      <div
        className={`ptr-indicator${isPulling ? ' dragging' : ''}`}
        style={{ height: pullDistance, opacity: Math.min(pullDistance / PULL_TRIGGER, 1) }}
      >
        <div className={`ptr-spinner${refreshing || pullDistance >= PULL_TRIGGER ? ' spin' : ''}`} />
      </div>

      <main className="feed">
        {loading && (
          <>
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </>
        )}

        {error && !loading && (
          <div className="error-state">
            <div className="error-icon">!</div>
            <p>{error}</p>
            <button className="retry-btn" onClick={() => fetchNews()}>
              Incearca din nou
            </button>
          </div>
        )}

        {!loading && !error && dedupedEntries
          .filter(entry => {
            if (activeFilters.length === 0) return true;
            const cats = entry.article.categories;
            return cats && cats.some(c => activeFilters.includes(c));
          })
          .filter(entry => {
            if (!searchQuery.trim()) return true;
            const q = searchQuery.toLowerCase();
            return entry.article.title.toLowerCase().includes(q) ||
                   (entry.article.description || '').toLowerCase().includes(q);
          })
          .slice(0, visibleCount)
          .map((entry) => (
            <ArticleCard
              key={entry.article.id}
              entry={entry}
              isRead={!!readIds[entry.article.id]}
              onClick={() => handleOpenArticle(entry)}
              showImage={showFeedImages}
              openShareId={openShareId}
              setOpenShareId={setOpenShareId}
            />
          ))}
      </main>

      {filterOpen && (
        <FilterPanel
          activeFilters={activeFilters}
          onToggle={(key) => setActiveFilters(prev =>
            prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
          )}
          onClear={() => setActiveFilters([])}
          onClose={() => setFilterOpen(false)}
        />
      )}

      {/* Search bar */}
      {searchOpen && (
        <div className="search-bar-container">
          <input
            className="search-input"
            type="text"
            placeholder="Caută știri..."
            autoFocus
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
          <button className="search-close-btn" onClick={() => { setSearchOpen(false); setSearchQuery(''); }}>&#x2715;</button>
        </div>
      )}

      {menuOpen && (
        <div className="menu-overlay" onClick={() => { setMenuOpen(false); setMenuPage('main'); }}>
          <div className="menu-panel" onClick={e => e.stopPropagation()}>

            {/* Header */}
            <div className="menu-header">
              {menuPage !== 'main' ? (
                <button className="menu-back-btn" onClick={() => setMenuPage('main')}>&#x2190;</button>
              ) : (
                <span className="menu-title">{getAppName()}</span>
              )}
              {menuPage !== 'main' && (
                <span className="menu-subtitle">
              {menuPage === 'notificari' ? 'Notificări' : menuPage === 'surse' ? 'Surse' : 'Setări'}
                </span>
              )}
              <button className="menu-close" onClick={() => { setMenuOpen(false); setMenuPage('main'); }}>&#x2715;</button>
            </div>

            {/* Main page */}
            {menuPage === 'main' && (
              <div className="menu-items">
                <button className="menu-item menu-item-nav" onClick={() => setMenuPage('notificari')}>
                  <span className="menu-item-icon">🔔</span>
                  <span className="menu-item-label">Notificări</span>
                  <span className="menu-item-arrow">›</span>
                </button>
                <button className="menu-item menu-item-nav" onClick={() => setMenuPage('surse')}>
                  <span className="menu-item-icon">📰</span>
                  <span className="menu-item-label">Surse</span>
                  <span className="menu-item-arrow">›</span>
                </button>
                <button className="menu-item menu-item-nav" onClick={() => setMenuPage('setari')}>
                  <span className="menu-item-icon">⚙️</span>
                  <span className="menu-item-label">Setări</span>
                  <span className="menu-item-arrow">›</span>
                </button>
                <button className="menu-item menu-item-nav" onClick={() => {}}>
                  <span className="menu-item-icon">🫶</span>
                  <span className="menu-item-label">Dă-ne o notă</span>
                  <span className="menu-item-arrow">›</span>
                </button>
              </div>
            )}

            {/* Notificări subpage */}
            {menuPage === 'notificari' && (
              <div className="menu-items">
                <div className="menu-item">
                  <div className="menu-item-text">
                    <span className="menu-item-label">Alerte Push</span>
                    <span className="menu-item-desc">Primești notificări pentru știri noi</span>
                  </div>
                  <button
                    className={`toggle-switch ${pushAlertsOn ? 'on' : 'off'}`}
                    onClick={async () => {
                      if (pushAlertsOn) {
                        setPushAlertsOn(false);
                        return;
                      }
                      if (!('Notification' in window)) return;
                      if (Notification.permission === 'granted') {
                        setPushAlertsOn(true);
                        return;
                      }
                      if (Notification.permission === 'denied') {
                        alert('Notificările sunt blocate în browser. Activează-le din setările browserului pentru acest site.');
                        return;
                      }
                      const permission = await Notification.requestPermission();
                      if (permission === 'granted') {
                        setPushAlertsOn(true);
                      } else {
                        setPushAlertsOn(false);
                      }
                    }}
                  >
                    <span className="toggle-knob" />
                  </button>
                </div>
                <div className="menu-item">
                  <div className="menu-item-text">
                    <span className="menu-item-label">Notificări 🔔</span>
                    <span className="menu-item-desc">Afișează contorul din header</span>
                  </div>
                  <button
                    className={`toggle-switch ${bellVisible ? 'on' : 'off'}`}
                    onClick={() => setBellVisible(v => !v)}
                  >
                    <span className="toggle-knob" />
                  </button>
                </div>
              </div>
            )}

            {/* Surse subpage */}
            {menuPage === 'surse' && (
              <div className="menu-items">
                {SOURCES.map(src => (
                  <div className="menu-item" key={src}>
                    <div className="menu-item-text">
                      <span className="menu-item-label">{SOURCE_META[src].label}</span>
                    </div>
                    <button
                      className={`toggle-switch ${enabledSources.includes(src) ? 'on' : 'off'}`}
                      onClick={() => {
                        const isCurrentlyOn = enabledSources.includes(src);
                        setEnabledSources(prev => {
                          const next = isCurrentlyOn
                            ? prev.length > 1 ? prev.filter(s => s !== src) : prev
                            : [...prev, src];
                          return next;
                        });
                        if (!isCurrentlyOn) fetchNews(true);
                      }}
                    >
                      <span className="toggle-knob" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Setări subpage */}
            {menuPage === 'setari' && (
              <div className="menu-items">
                <div className="menu-item">
                  <div className="menu-item-text">
                    <span className="menu-item-label">Imagini în feed</span>
                    <span className="menu-item-desc">Afișează imaginile în lista de știri</span>
                  </div>
                  <button
                    className={`toggle-switch ${showFeedImages ? 'on' : 'off'}`}
                    onClick={() => setShowFeedImages(v => !v)}
                  >
                    <span className="toggle-knob" />
                  </button>
                </div>
                <div className="menu-item">
                  <div className="menu-item-text">
                    <span className="menu-item-label">Imagini în articol</span>
                    <span className="menu-item-desc">Afișează imaginile în articol</span>
                  </div>
                  <button
                    className={`toggle-switch ${showArticleImages ? 'on' : 'off'}`}
                    onClick={() => setShowArticleImages(v => !v)}
                  >
                    <span className="toggle-knob" />
                  </button>
                </div>
                <button className="menu-item menu-item-nav" onClick={() => {
                  Object.keys(localStorage)
                    .filter(k => k.startsWith('blitz_'))
                    .forEach(k => localStorage.removeItem(k));
                  setMenuOpen(false);
                  setMenuPage('main');
                  fetchNews(false);
                }}>
                  <span className="menu-item-icon">🗑️</span>
                  <span className="menu-item-label" style={{color: '#e05555'}}>Golește cache</span>
                </button>
              </div>
            )}

            {/* Bottom bar: search + theme */}
            <div className="menu-bottom-bar">
              <button className="menu-bottom-icon" onClick={() => { setMenuOpen(false); setMenuPage('main'); setSearchOpen(true); }} aria-label="Caută">
                🔍
              </button>
              <button className="menu-bottom-icon theme-icon" onClick={() => setDarkMode(!darkMode)} aria-label="Toggle theme">
                {darkMode ? '🌙' : '☀️'}
              </button>
            </div>

          </div>
        </div>
      )}
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatTimeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'acum';
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  return `${days}z`;
}
